import {
    Add as AddIcon,
    ChevronLeft,
    ChevronRight,
    Delete as DeleteIcon,
    Edit as EditIcon,
    PictureAsPdf as PictureAsPdfIcon
} from '@mui/icons-material';
import {
    Autocomplete,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    Grid,
    IconButton,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { API_URL } from '../config/api';
import EventStaffAssignment from './EventStaffAssignment';
import VisitsReportPanel from './VisitsReportPanel';

const LEGACY_AWARENESS_SOURCE_LABELS = {
    yandex_afisha: 'Яндекс Афиша',
    mts_live: 'МТС live',
    friends: 'Друзья',
    metro: 'Метро',
    vk: 'ВК',
    instagram: 'Инст',
    tiktok: 'Тик-ток',
    passing_by: 'Проходили мимо',
    already_visited: 'Уже были',
    internet: 'Интернет'
};

const AWARENESS_SOURCE_OPTIONS = [
    'Яндекс Афиша',
    'МТС live',
    'Кассир',
    'Сайт',
    'Друзья',
    'Метро',
    'ВК',
    'Инст',
    'Тик-ток',
    'Телеграм',
    'Проходили мимо',
    'Уже были',
    'Интернет'
];

const PRODUCT_TYPE_OPTIONS = [
    'Экскурсия',
    'Индивидуальная экскурсия',
    'Свидание',
    'Школьная группа',
    'Тренинг'
];

const PRODUCT_TYPE_DEFAULT_CAPACITY = {
    'Экскурсия': 8,
    'Свидание': 2,
    'Индивидуальная экскурсия': 2,
    'Школьная группа': 8,
    'Тренинг': 8
};

const PAYMENT_METHOD_LABELS = {
    cash: '💵 Наличные',
    card: '💳 Карта',
    online: '🌐 Онлайн',
    sbp: '🏦 СБП',
    yandex: '🎫 Яндекс',
    invoice: '📄 По счету',
    free: '🎁 Бесплатно'
};

const salePaymentTag = (method) => {
    if (method === 'cash') return ' · НАЛ';
    if (method === 'invoice') return ' · СЧЕТ';
    return ' · БЕЗНАЛ';
};

const extrasForOrder = (extras, orderId) =>
    (extras || []).filter((row) => String(row.order_id) === String(orderId || ''));

const extrasTotal = (extras) =>
    (extras || []).reduce((sum, extra) => sum + Number(extra.total_price || (Number(extra.unit_price) || 0) * (Number(extra.quantity) || 0) || 0), 0);

const EXTRA_CATEGORY_EMOJIS = {
    bundle: '🍱',
    'Наборы': '🍱',
    наборы: '🍱',
    десерты: '🍰',
    напитки: '☕'
};

const MENU_CATEGORIES = ['Наборы', 'десерты', 'напитки'];

const isTruthyFlag = (value) => value === true || value === 't' || value === 'true' || value === 1 || value === '1';

const getYandexSaleState = (event, liveAvailable) => {
    // Явно переданное живое число свободных мест (используется в карточке слота)
    let live = liveAvailable;

    // Живые данные, подгруженные для списка слотов дня
    if ((live == null || live === '') && event) {
        if (event.live_error) {
            return { open: false, label: 'Нет данных', color: 'default' };
        }
        if (event.live_free != null) {
            live = event.live_free;
        }
    }

    // Событие не активно в Яндексе — считаем закрытым
    if (event && Number(event.status) !== 1) {
        return { open: false, label: 'Закрыто', color: 'error' };
    }

    if (live != null && live !== '') {
        const free = Number(live);
        if (Number.isFinite(free)) {
            return free > 0
                ? { open: true, label: 'Открыто', color: 'success' }
                : { open: false, label: 'Закрыто', color: 'error' };
        }
    }

    if (!event) {
        return { open: false, label: 'Закрыто', color: 'error' };
    }

    // Фолбэк: расчёт по данным CRM (когда живых данных нет)
    // Если в CRM нет ни продаж, ни служебных закрытий — реальное состояние слота неизвестно
    if (Number(event.tickets_sold || 0) === 0 && Number(event.slot_close_count || 0) === 0) {
        return { open: false, label: 'Нет данных', color: 'default' };
    }

    if (isTruthyFlag(event.is_yandex_closed) || Number(event.slot_close_count || 0) > 0) {
        return { open: false, label: 'Закрыто', color: 'error' };
    }

    const free = Number(event.yandex_available ?? event.available_tickets ?? 0);
    if (free <= 0) {
        return { open: false, label: 'Закрыто', color: 'error' };
    }

    return { open: true, label: 'Открыто', color: 'success' };
};

const getAwarenessSourceDisplay = (value) => {
    if (!value) {
        return '';
    }

    return LEGACY_AWARENESS_SOURCE_LABELS[value] || value;
};

const getVisitSourceDisplay = (source) => {
    const value = typeof source === 'string' ? source : (source?.display_name || source?.name || source?.source_name || '');
    const normalized = value.toLowerCase();

    if (normalized === 'walking' || normalized === 'walk' || normalized === 'улица') {
        return 'улица';
    }

    if (normalized === 'yandex' || value === 'Яндекс.Билеты') {
        return 'Яндекс.Билеты';
    }

    return value.charAt(0).toUpperCase() + value.slice(1);
};

const getExtraEmoji = (extra) => {
    if (!extra) {
        return '🍽️';
    }

    if (extra.item_type === 'bundle') {
        return EXTRA_CATEGORY_EMOJIS.bundle;
    }

    const normalizedCategory = (extra.category || '').toLowerCase();
    return EXTRA_CATEGORY_EMOJIS[normalizedCategory] || '🍽️';
};

const getDefaultCapacityForProduct = (productType) => PRODUCT_TYPE_DEFAULT_CAPACITY[productType] || 8;

const willCloseYandexRemainder = (productType, ticketCount) => {
    const type = String(productType || '').trim().toLowerCase();
    if (type.includes('индивидуал') || type.includes('свидан') || type.includes('школьн') || type.includes('тренинг')) {
        return true;
    }
    return Number(ticketCount) >= 8;
};

const pickDefaultYandexRate = (rates, tickets = []) => {
    if (!rates?.length) {
        return null;
    }

    return rates.find((rate) => /взросл/i.test(rate.name || '')) || rates[0] || {
        id: 46893501,
        name: 'Взрослый билет',
        price: 1000
    };
};

const ticketFromRate = (rate, quantity = 1) => ({
    rate_id: rate?.id ?? null,
    rate_name: rate?.name || '',
    price: rate?.price || 0,
    quantity,
    sector: 'Входной билет'
});

const isPackageProductType = (productType) => {
    const type = String(productType || '').trim().toLowerCase();
    return type.includes('индивидуал') || type.includes('свидан') || type.includes('школьн') || type.includes('тренинг');
};

const getDayKind = (eventDate) => {
    const date = eventDate ? new Date(eventDate) : new Date();
    if (Number.isNaN(date.getTime())) return 'weekday';
    const day = date.getUTCDay();
    return (day === 0 || day === 6) ? 'weekend' : 'weekday';
};

const getEventTimeHm = (eventDate) => {
    const date = eventDate ? new Date(eventDate) : new Date();
    if (Number.isNaN(date.getTime())) return '00:00';
    return `${String(date.getUTCHours()).padStart(2, '0')}:${String(date.getUTCMinutes()).padStart(2, '0')}`;
};

const ticketAudience = (name) => {
    const n = String(name || '').toLowerCase();
    if (/студент/.test(n)) return 'student';
    if (/детск/.test(n)) return 'child';
    if (/взросл/.test(n)) return 'adult';
    return 'other';
};

const matchYandexRate = (rates, crmName) => {
    const audience = ticketAudience(crmName);
    const list = rates || [];
    if (audience === 'student') return list.find((rate) => /студент/.test(rate.name || '')) || list[0] || null;
    if (audience === 'child') return list.find((rate) => /детск/.test(rate.name || '')) || list[0] || null;
    if (audience === 'adult') return list.find((rate) => /взросл/.test(rate.name || '')) || list[0] || null;
    return list[0] || null;
};

const isTicketRowForEvent = (row, eventDate) => {
    const day = getDayKind(eventDate);
    if (row.day_kind && row.day_kind !== 'any' && row.day_kind !== day) return false;
    if (row.time_until) {
        const until = String(row.time_until).slice(0, 5);
        if (until && getEventTimeHm(eventDate) >= until) return false;
    }
    return true;
};

const getTicketCatalog = (priceList, eventDate) => {
    const tickets = (priceList || []).filter((row) => row.kind === 'ticket');
    return tickets.slice().sort((a, b) => {
        const aFit = isTicketRowForEvent(a, eventDate) ? 0 : 1;
        const bFit = isTicketRowForEvent(b, eventDate) ? 0 : 1;
        if (aFit !== bFit) return aFit - bFit;
        return (Number(a.sort_order) || 0) - (Number(b.sort_order) || 0);
    });
};

const matchesProductType = (row, productType) => {
    const type = String(productType || '').toLowerCase();
    const label = String(row.product_type || row.name || '').toLowerCase();
    if (!type || !label) return false;
    if (type.includes('индивидуал')) return label.includes('индивидуал');
    if (type.includes('свидан')) return label.includes('свидан');
    if (type.includes('школьн')) return label.includes('школьн');
    if (type.includes('тренинг')) return label.includes('тренинг');
    return label.includes(type) || type.includes(label);
};

const getSlotCatalogPrice = (priceList, productType, eventDate) => {
    if (!isPackageProductType(productType)) return 0;
    const day = getDayKind(eventDate);
    const rows = (priceList || []).filter((row) => {
        if (row.kind !== 'slot') return false;
        if (row.day_kind && row.day_kind !== 'any' && row.day_kind !== day) return false;
        return matchesProductType(row, productType);
    });
    return Number(rows[0]?.price) || 0;
};

const ticketFromCrmRow = (crmRow, yandexRates, quantity = 1) => {
    const yandexRate = matchYandexRate(yandexRates, crmRow?.name);
    return {
        rate_id: yandexRate?.id ?? null,
        rate_name: crmRow?.name || yandexRate?.name || '',
        price: Number(crmRow?.price) || 0,
        quantity,
        sector: 'Входной билет',
        crm_price_id: crmRow?.id || null
    };
};

const getBookingTicketTotal = (tickets, productType, priceList, eventDate) => {
    if (isPackageProductType(productType)) {
        const sent = (tickets || []).reduce((sum, ticket) => sum + (Number(ticket.price) || 0), 0);
        if (sent > 0) return sent;
        return getSlotCatalogPrice(priceList, productType, eventDate);
    }
    return (tickets || []).reduce((sum, ticket) => sum + (Number(ticket.price) || 0) * (ticket.quantity || 1), 0);
};

const defaultBookingTicket = (productType, priceList, eventDate, yandexRates) => {
    if (isPackageProductType(productType)) {
        const rate = pickDefaultYandexRate(yandexRates, []);
        const pack = getSlotCatalogPrice(priceList, productType, eventDate);
        return { ...ticketFromRate(rate, 1), price: pack };
    }
    const catalog = getTicketCatalog(priceList, eventDate);
    const matching = catalog.filter((row) => isTicketRowForEvent(row, eventDate));
    const adult = matching.find((row) => ticketAudience(row.name) === 'adult')
        || matching[0]
        || catalog.find((row) => ticketAudience(row.name) === 'adult')
        || catalog[0];
    if (adult) return ticketFromCrmRow(adult, yandexRates, 1);
    const rate = pickDefaultYandexRate(yandexRates, []);
    return ticketFromRate(rate, 1);
};

const VisitsPanel = ({ token, user }) => {
    const [currentDate, setCurrentDate] = useState(new Date());
    const [calendarData, setCalendarData] = useState([]);
    const [stats, setStats] = useState([]);
    const [visits, setVisits] = useState([]);
    const [sources, setSources] = useState([]);
    const [awarenessOptions, setAwarenessOptions] = useState(AWARENESS_SOURCE_OPTIONS);
    const [priceList, setPriceList] = useState([]);
    const [paymentOptions, setPaymentOptions] = useState(['cash', 'card', 'online', 'sbp', 'yandex', 'invoice', 'free']);
    const [openDialog, setOpenDialog] = useState(false);
    const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
    const [openDayDialog, setOpenDayDialog] = useState(false);
    const [selectedDayData, setSelectedDayData] = useState(null);
    const [selectedDayVisits, setSelectedDayVisits] = useState([]);
    const [dayEvents, setDayEvents] = useState([]);
    const [dayAdmins, setDayAdmins] = useState([]);
    const [selectedDayAdminId, setSelectedDayAdminId] = useState('');
    const [showHiddenSlots, setShowHiddenSlots] = useState(false);
    const [showReport, setShowReport] = useState(false);
    const [syncingMonth, setSyncingMonth] = useState(false);
    const [syncMonthMsg, setSyncMonthMsg] = useState('');

    // Новый диалог для управления конкретным событием
    const [openEventDialog, setOpenEventDialog] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [eventVisitors, setEventVisitors] = useState([]);
    const [eventMenu, setEventMenu] = useState([]);
    const [eventExtras, setEventExtras] = useState([]);

    // Диалог добавления блюда
    const [openAddMenuDialog, setOpenAddMenuDialog] = useState(false);
    const [availableMenuItems, setAvailableMenuItems] = useState([]);
    const [selectedMenuItem, setSelectedMenuItem] = useState('');
    const [menuCategoryFilter, setMenuCategoryFilter] = useState('');
    const [menuQuantity, setMenuQuantity] = useState(1);

    // Диалог добавления посетителя
    const [openAddVisitorDialog, setOpenAddVisitorDialog] = useState(false);
    const [newVisitor, setNewVisitor] = useState({
        visitor_name: '',
        visitor_phone: '',
        visitor_email: '',
        payment_method: 'cash',
        awareness_source: '',
        visit_status: '',
        payment_status: '',
        notes: ''
    });

    // Карточка клиента (по заказу)
    const [openClientDialog, setOpenClientDialog] = useState(false);
    const [clientDraft, setClientDraft] = useState({
        order_id: null,
        visitor_name: '',
        visitor_phone: '',
        visitor_email: '',
        payment_method: 'cash',
        awareness_source: '',
        visit_status: '',
        payment_status: '',
        notes: '',
        people_count: 1,
        original_people_count: 1,
        extra_payment_method: 'cash'
    });
    const [clientExtras, setClientExtras] = useState([]);
    const [clientExtraDraft, setClientExtraDraft] = useState({
        menu_item_id: '',
        quantity: 1,
        payment_method: 'cash',
        notes: ''
    });
    const [visitorExtras, setVisitorExtras] = useState([]);
    const [visitorExtraDraft, setVisitorExtraDraft] = useState({
        menu_item_id: '',
        quantity: 1,
        payment_method: 'cash',
        notes: ''
    });

    // Список билетов для добавления
    const [ticketsList, setTicketsList] = useState([
        { rate_name: 'Взрослый билет', price: 1250, quantity: 1, sector: 'Входной билет' }
    ]);

    // Диалог подтверждения удаления
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [ticketToDelete, setTicketToDelete] = useState(null);

    // Свободные билеты и тарифы из Яндекса
    const [availableRates, setAvailableRates] = useState([]);
    const [availableTicketsCount, setAvailableTicketsCount] = useState(0);
    const [availableTicketsError, setAvailableTicketsError] = useState('');
    const [bookingInProgress, setBookingInProgress] = useState(false);
    const [yandexSaleBusy, setYandexSaleBusy] = useState(false);
    const [openSeatsDialog, setOpenSeatsDialog] = useState(false);
    const [openSeatsCount, setOpenSeatsCount] = useState(1);
    const [openSeatsBusy, setOpenSeatsBusy] = useState(false);

    // Назначение сотрудников/гидов на слот
    const [openStaffDialog, setOpenStaffDialog] = useState(false);
    const [eventStaff, setEventStaff] = useState([]);
    const [eventSales, setEventSales] = useState([]);
    const [salesSaving, setSalesSaving] = useState(false);
    const [salesDraft, setSalesDraft] = useState({
        employee_id: '',
        notes: ''
    });
    const [availableSouvenirItems, setAvailableSouvenirItems] = useState([]);
    const [souvenirCart, setSouvenirCart] = useState([]);
    const [selectedSouvenirId, setSelectedSouvenirId] = useState('');
    const [souvenirQty, setSouvenirQty] = useState(1);
    const [souvenirPaymentDraft, setSouvenirPaymentDraft] = useState('cash');
    const [barCart, setBarCart] = useState([]);
    const [selectedBarItemId, setSelectedBarItemId] = useState('');
    const [barQty, setBarQty] = useState(1);
    const [barPaymentDraft, setBarPaymentDraft] = useState('cash');
    const [barDestinationDraft, setBarDestinationDraft] = useState('before_after');

    const [newVisit, setNewVisit] = useState({
        visit_date: new Date().toISOString().split('T')[0],
        source_name: 'yandex',
        visitor_name: '',
        visitor_phone: '',
        visitor_email: '',
        tickets_count: 1,
        total_amount: 0,
        notes: '',
        awareness_source: ''
    });

    const canSync = ['owner', 'admin', 'manager'].includes(user?.role);

    const getMonthSyncRange = () => {
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth();
        const dateFrom = `${year}-${String(month + 1).padStart(2, '0')}-01`;
        const dateTo = `${year}-${String(month + 1).padStart(2, '0')}-${String(new Date(year, month + 1, 0).getDate()).padStart(2, '0')}`;
        return { dateFrom, dateTo };
    };

    const handleSyncCurrentMonth = async () => {
        if (!canSync) {
            setSyncMonthMsg('❌ Недостаточно прав для синхронизации');
            return;
        }

        setSyncingMonth(true);
        setSyncMonthMsg('');
        try {
            const { dateFrom, dateTo } = getMonthSyncRange();
            const headers = {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`
            };

            await apiFetch(`${API_URL}/api/yandex/sync-events`, { method: 'POST', headers });
            const ordersResponse = await apiFetch(`${API_URL}/api/yandex/sync-orders-to-visits`, {
                method: 'POST',
                headers,
                body: JSON.stringify({ date_from: dateFrom, date_to: dateTo })
            });
            const ordersData = await readJson(ordersResponse);

            const ticketsResponse = await apiFetch(`${API_URL}/api/yandex/sync-tickets`, {
                method: 'POST',
                headers,
                body: JSON.stringify({ date_from: dateFrom, date_to: dateTo })
            });
            const ticketsData = await readJson(ticketsResponse);

            const syncedTickets = ticketsData.synced ?? ((ticketsData.inserted || 0) + (ticketsData.updated || 0));
            const monthLabel = currentDate.toLocaleString('ru-RU', { month: 'long' });
            if (ordersData.success) {
                const ticketNote = ticketsData.success
                    ? `, билеты ${syncedTickets}`
                    : ticketsData.error
                        ? ` · билеты позже: ${ticketsData.error}`
                        : '';
                setSyncMonthMsg(`✅ ${monthLabel}: заказы ${ordersData.inserted || 0}${ticketNote}`);
            } else {
                setSyncMonthMsg(`❌ ${ordersData.message || ordersData.error || ticketsData.error || 'Синхронизация не выполнена'}`);
            }

            await fetchCalendarData();
            await fetchStats();
            if (openDayDialog && selectedDayData?.date) {
                await fetchDayEvents(selectedDayData.date);
            }
        } catch (error) {
            console.error('Error syncing month:', error);
            setSyncMonthMsg('❌ Ошибка синхронизации месяца');
        } finally {
            setSyncingMonth(false);
        }
    };

    const apiFetch = (url, options = {}) => fetch(url, {
        ...options,
        headers: {
            ...(options.headers || {}),
            Authorization: `Bearer ${token}`
        }
    });

    // Безопасное чтение JSON: 304 (пустое тело) и не-JSON не должны ломать интерфейс
    const readJsonSafe = async (response) => {
        if (!response || response.status === 304) return null;
        try {
            const text = await response.text();
            if (!text) return null;
            return JSON.parse(text);
        } catch (error) {
            console.warn('Не удалось разобрать ответ сервера:', error?.message);
            return null;
        }
    };

    const readJson = async (response) => {
        const text = await response.text();
        if (!text) {
            return { success: false, error: `Пустой ответ сервера (${response.status})` };
        }
        try {
            return JSON.parse(text);
        } catch {
            if (response.status === 504 || /timeout|gateway/i.test(text)) {
                return {
                    success: false,
                    error: 'Сервер не успел догрузить данные из Яндекса. Календарь мог уже обновиться — обновите страницу.'
                };
            }
            return { success: false, error: `Ответ сервера ${response.status}` };
        }
    };

    const loadDayAdmins = async () => {
        if (!token) {
            setDayAdmins([]);
            return;
        }

        try {
            const response = await apiFetch(`${API_URL}/api/employees`);
            const data = await response.json();
            const list = Array.isArray(data) ? data : (data.data || data.employees || []);
            const filtered = list.filter((employee) => {
                const position = (employee.position || '').toLowerCase();
                return position.includes('админ') || position.includes('менедж') || position.includes('владел');
            });
            setDayAdmins(filtered.length ? filtered : list);
        } catch (error) {
            console.error('Error loading day admins:', error);
        }
    };

    const fetchSelectedDayAdmin = async (date) => {
        if (!token || !date) {
            setSelectedDayAdminId('');
            return;
        }

        try {
            const response = await apiFetch(`${API_URL}/api/admin-shifts?date=${date}`);
            const data = await response.json();
            const shifts = data.success ? (data.data || []) : [];
            setSelectedDayAdminId(shifts[0]?.employee_id ? String(shifts[0].employee_id) : '');
        } catch (error) {
            console.error('Error loading selected day admin:', error);
            setSelectedDayAdminId('');
        }
    };

    const syncDayAdminWithVisibleSlots = async (employeeId, previousShifts = []) => {
        if (!selectedDayData?.date || dayEvents.length === 0) {
            return;
        }

        const previousEmployeeIds = previousShifts
            .map((shift) => shift.employee_id)
            .filter(Boolean);

        await Promise.all(previousEmployeeIds.flatMap((previousEmployeeId) =>
            dayEvents.map((event) => apiFetch(`${API_URL}/api/events/${event.id}/staff/${previousEmployeeId}`, {
                method: 'DELETE'
            }))
        ));

        if (!employeeId) {
            return;
        }

        await Promise.all(dayEvents.map((event) => apiFetch(`${API_URL}/api/events/${event.id}/staff`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                employeeIds: [Number(employeeId)],
                notes: `Администратор дня ${selectedDayData.date}`
            })
        })));
    };

    const handleDayAdminChange = async (employeeId) => {
        if (!token || !selectedDayData?.date) {
            return;
        }

        try {
            const currentResponse = await apiFetch(`${API_URL}/api/admin-shifts?date=${selectedDayData.date}`);
            const currentData = await currentResponse.json();
            const currentShifts = currentData.success ? (currentData.data || []) : [];

            await Promise.all(currentShifts.map((shift) => apiFetch(`${API_URL}/api/admin-shifts/${shift.id}`, {
                method: 'DELETE'
            })));

            if (employeeId) {
                await apiFetch(`${API_URL}/api/admin-shifts`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        shift_date: selectedDayData.date,
                        employee_id: Number(employeeId),
                        notes: 'Назначено из CRM слотов'
                    })
                });
            }

            await syncDayAdminWithVisibleSlots(employeeId, currentShifts);
            setSelectedDayAdminId(employeeId);
            if (selectedEvent) {
                await fetchEventStaff(selectedEvent.id);
            }
        } catch (error) {
            console.error('Error updating day admin:', error);
            alert('Не удалось назначить администратора на день');
        }
    };

    // Загрузка данных календаря
    const fetchCalendarData = async () => {
        try {
            const month = currentDate.getMonth() + 1;
            const year = currentDate.getFullYear();

            console.log('🔍 Fetching calendar data for:', month, year);

            const response = await apiFetch(
                `${API_URL}/api/visits/calendar?month=${month}&year=${year}`
            );
            const data = await response.json();

            console.log('📅 Calendar data received:', data);

            if (data.success) {
                setCalendarData(data.data);
                console.log('✅ Calendar data set:', data.data);
            }
        } catch (error) {
            console.error('❌ Error fetching calendar data:', error);
        }
    };

    // Загрузка статистики
    const fetchStats = async () => {
        try {
            const response = await apiFetch(`${API_URL}/api/visits/stats`);
            const data = await response.json();

            if (data.success) {
                setStats(data.data);
            }
        } catch (error) {
            console.error('Error fetching stats:', error);
        }
    };

    // Загрузка списка посещений
    const fetchVisits = async () => {
        try {
            const response = await apiFetch(`${API_URL}/api/visits`);
            const data = await response.json();

            if (data.success) {
                setVisits(data.data);
            }
        } catch (error) {
            console.error('Error fetching visits:', error);
        }
    };

    // Загрузка источников
    const fetchSources = async () => {
        try {
            const response = await apiFetch(`${API_URL}/api/visits/sources`);
            const data = await response.json();

            if (data.success) {
                const allowedSources = data.data || [];
                setSources(allowedSources);
                setNewVisit((prev) => {
                    if (!allowedSources.length || allowedSources.some((source) => source.name === prev.source_name)) {
                        return prev;
                    }
                    return { ...prev, source_name: allowedSources[0].name };
                });
            }
        } catch (error) {
            console.error('Error fetching sources:', error);
        }
    };

    const fetchAwarenessOptions = async () => {
        try {
            const response = await apiFetch(`${API_URL}/api/settings/options/awareness_sources`);
            const data = await response.json();
            if (data.success) {
                setAwarenessOptions((data.options || []).map((option) => option.label || option.value));
            }
        } catch (error) {
            console.error('Error fetching awareness options:', error);
        }
    };

    const fetchPriceList = async () => {
        try {
            const response = await apiFetch(`${API_URL}/api/settings/price-list`);
            const data = await response.json();
            const prices = data.success ? (data.prices || []) : [];
            if (data.success) {
                setPriceList(prices);
            }
            return prices;
        } catch (error) {
            console.error('Error fetching price list:', error);
            return priceList;
        }
    };

    const fetchPaymentOptions = async () => {
        try {
            const response = await apiFetch(`${API_URL}/api/settings/options/payment_methods`);
            const data = await response.json();
            if (data.success) {
                setPaymentOptions((data.options || []).map((option) => option.value));
            }
        } catch (error) {
            console.error('Error fetching payment options:', error);
        }
    };

    const saveAwarenessOption = async (value) => {
        const normalized = (value || '').trim();
        if (!normalized) {
            return;
        }

        try {
            await apiFetch(`${API_URL}/api/settings/options/awareness_sources`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ value: normalized, label: normalized })
            });
            setAwarenessOptions((prev) => prev.includes(normalized) ? prev : [...prev, normalized]);
        } catch (error) {
            console.error('Error saving awareness option:', error);
        }
    };

    // Загрузка посещений за конкретный день
    const fetchDayVisits = async (date) => {
        try {
            const response = await apiFetch(`${API_URL}/api/visits?startDate=${date}&endDate=${date}`);
            const data = await response.json();

            if (data.success) {
                setSelectedDayVisits(data.data);
            }
        } catch (error) {
            console.error('Error fetching day visits:', error);
        }
    };

    // Живое состояние слотов дня в Яндексе (реально открытые места)
    const fetchDayEventsLive = async (date) => {
        try {
            const params = new URLSearchParams({ date_from: date, date_to: date });
            const response = await apiFetch(`${API_URL}/api/yandex/events-live?${params.toString()}`);
            const data = await response.json();
            if (!data.success) return;

            const availability = data.availability || {};
            const applyLive = (event) => {
                const live = availability[String(event.id)];
                if (!live) return event;
                return {
                    ...event,
                    live_free: typeof live.free === 'number' ? live.free : null,
                    live_total: typeof live.total === 'number' ? live.total : null,
                    live_sold: typeof live.sold === 'number' ? live.sold : null,
                    live_error: live.error || null
                };
            };

            setDayEvents((prev) => prev.map(applyLive));
            setSelectedEvent((prev) => (prev?.id ? applyLive(prev) : prev));
        } catch (error) {
            console.error('Error fetching live day events:', error);
        }
    };

    // Загрузка событий за конкретный день
    const fetchDayEvents = async (date) => {
        try {
            const params = new URLSearchParams({
                date_from: date,
                date_to: date
            });
            if (showHiddenSlots) {
                params.set('include_archived', 'true');
            }
            const response = await apiFetch(`${API_URL}/api/yandex/events?${params.toString()}`);
            const data = await response.json();

            if (data.success) {
                const events = data.events || [];

                const eventsWithAvailability = events.map(event => ({
                    ...event,
                    available_tickets: parseInt(event.yandex_available ?? event.tickets_available, 10) || 0,
                    is_yandex_closed: isTruthyFlag(event.is_yandex_closed),
                    slot_close_count: parseInt(event.slot_close_count, 10) || 0,
                    staff: Array.isArray(event.staff)
                        ? event.staff
                        : (typeof event.staff === 'string' ? JSON.parse(event.staff || '[]') : [])
                }));

                setDayEvents(eventsWithAvailability);
                setSelectedEvent((prev) => {
                    if (!prev?.id) return prev;
                    const updated = eventsWithAvailability.find((event) => String(event.id) === String(prev.id));
                    return updated ? { ...prev, ...updated } : prev;
                });

                // Подтягиваем реальные данные из Яндекса (не блокируя отрисовку списка)
                fetchDayEventsLive(date);

                return eventsWithAvailability;
            } else {
                setDayEvents([]);
            }
        } catch (error) {
            console.error('Error fetching day events:', error);
            setDayEvents([]);
        }
    };

    // Загрузка посетителей конкретного события
    const fetchEventVisitors = async (eventId, retryAfterRefresh = true) => {
        try {
            const response = await apiFetch(`${API_URL}/api/yandex/event/${eventId}/tickets`);
            const data = await readJsonSafe(response);
            // Пустой ответ/304 — не повод обнулять уже показанный список
            if (!data) return;

            setEventVisitors(data.tickets || []);
            setEventExtras(data.extras || []);
            // Сервер в фоне сверяет аннулированные заказы с Яндексом — перезапросим один раз
            if (data.refreshing && retryAfterRefresh) {
                setTimeout(() => fetchEventVisitors(eventId, false), 8000);
            }
        } catch (error) {
            console.error('Error fetching event visitors:', error);
            setEventVisitors([]);
            setEventExtras([]);
        }
    };

    // Обработчик клика на день в календаре
    const handleDayClick = (day) => {
        if (!day) return;

        const dateStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
        const dayData = getDayData(day) || {
            date: dateStr,
            sources: [],
            total_visits: 0,
            total_tickets: 0,
            total_amount: 0,
            slots_count: 0
        };

        setSelectedDayData({ ...dayData, date: dateStr });
        fetchDayEvents(dateStr);
        fetchSelectedDayAdmin(dateStr);
        setOpenDayDialog(true);
    };

    // Обработчик клика на событие
    const handleEventClick = async (event) => {
        setSelectedEvent(event);
        setAvailableRates([]);
        setAvailableTicketsError('');
        setTicketsList([
            { rate_name: '', price: 0, quantity: 1, sector: 'Входной билет' }
        ]);
        setSalesDraft({
            employee_id: selectedDayAdminId || '',
            notes: ''
        });
        setSouvenirCart([]);
        setSelectedSouvenirId('');
        setSouvenirQty(1);
        setSouvenirPaymentDraft('cash');
        setBarCart([]);
        setSelectedBarItemId('');
        setBarQty(1);
        setBarPaymentDraft('cash');
        setBarDestinationDraft('before_after');
        await fetchAvailableSouvenirItems();
        await fetchAvailableMenuItems();
        await fetchEventVisitors(event.id);
        await fetchEventMenu(event.id);
        await fetchAvailableTickets(event.id);
        await fetchEventStaff(event.id);
        await fetchEventSales(event.id);
        setOpenEventDialog(true);
        // Обновляем живые данные Яндекса по дню, чтобы в карточке слота были актуальные места
        const eventDate = String(event.event_date || '').slice(0, 10);
        if (eventDate) {
            fetchDayEventsLive(eventDate);
        }
    };

    const handleSlotVisibilityChange = async (eventId, isHidden) => {
        try {
            const response = await apiFetch(`${API_URL}/api/yandex/event/${eventId}/archive`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ is_archived: isHidden })
            });
            const data = await response.json();

            if (!data.success) {
                alert(data.error || (isHidden ? 'Не удалось скрыть слот в CRM' : 'Не удалось вернуть слот в CRM'));
                return;
            }

            if (selectedDayData?.date) {
                await fetchDayEvents(selectedDayData.date);
            }
        } catch (error) {
            console.error('Error archiving event:', error);
            alert(isHidden ? 'Не удалось скрыть слот в CRM' : 'Не удалось вернуть слот в CRM');
        }
    };

    const handleYandexSale = async (action) => {
        if (!selectedEvent?.id) return;
        const confirmText = action === 'close'
            ? 'Закрыть свободные места этого слота в Яндексе? Живые записи не трогаем.'
            : 'Открыть слот в Яндексе снова? Служебное закрытие снимется, свободные места вернутся в продажу.';
        if (!window.confirm(confirmText)) return;

        setYandexSaleBusy(true);
        try {
            const response = await apiFetch(`${API_URL}/api/yandex/event/${selectedEvent.id}/yandex-sale`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ action })
            });
            const data = await response.json();
            if (!data.success) {
                alert(data.error || 'Не удалось изменить продажу в Яндексе');
                return;
            }
            alert(data.message || (action === 'close' ? 'Слот закрыт в Яндексе' : 'Слот открыт в Яндексе'));
            if (action === 'close') {
                const remaining = Number.isFinite(Number(data.remaining)) ? Number(data.remaining) : null;
                setAvailableTicketsCount(remaining ?? 0);
                setSelectedEvent((prev) => prev ? {
                    ...prev,
                    available_tickets: remaining ?? prev.available_tickets,
                    yandex_available: remaining ?? prev.yandex_available,
                    is_yandex_closed: data.is_yandex_closed !== false,
                    slot_close_count: Math.max(Number(prev.slot_close_count) || 0, Number(data.closed) || 1)
                } : prev);
            }
            await fetchAvailableTickets(selectedEvent.id);
            await fetchEventVisitors(selectedEvent.id);
            if (selectedDayData?.date) {
                await fetchDayEvents(selectedDayData.date);
            }
        } catch (error) {
            console.error('Error changing Yandex sale:', error);
            alert('Не удалось изменить продажу в Яндексе');
        } finally {
            setYandexSaleBusy(false);
        }
    };

    const handleAddSeatsToSale = async () => {
        if (!selectedEvent?.id) {
            alert('Сначала откройте слот');
            return;
        }
        const count = Math.max(1, parseInt(openSeatsCount, 10) || 1);

        setOpenSeatsBusy(true);
        try {
            const response = await apiFetch(`${API_URL}/api/yandex/event/${selectedEvent.id}/yandex-sale`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ action: 'open', count })
            });
            const data = await response.json();
            if (!data.success) {
                alert(data.error || 'Не удалось добавить места в продажу');
                return;
            }
            setOpenSeatsDialog(false);
            alert(data.warning
                ? `${data.message}\n\nВнимание: ${data.warning}`
                : (data.message || `Добавлено в продажу ${count} мест`));
            await fetchAvailableTickets(selectedEvent.id);
            await fetchEventVisitors(selectedEvent.id);
            if (selectedDayData?.date) {
                await fetchDayEvents(selectedDayData.date);
                await fetchDayEventsLive(selectedDayData.date);
            }
        } catch (error) {
            console.error('Error adding seats to Yandex sale:', error);
            alert('Не удалось добавить места в продажу');
        } finally {
            setOpenSeatsBusy(false);
        }
    };

    // Кнопка «Добавить билеты в продажу» имеет смысл только там, где слот закрывала сама CRM
    // (есть служебные билеты «Закрытие слота»). Если данных нет — кнопку не блокируем.
    const slotHasCloseTickets = (event) => {
        const count = Number(event?.slot_close_count);
        return Number.isFinite(count) ? count > 0 : true;
    };

    const handleSaveEventSettings = async () => {
        if (!selectedEvent?.id) {
            return;
        }

        try {
            const response = await apiFetch(`${API_URL}/api/yandex/event/${selectedEvent.id}/settings`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    max_tickets: selectedEvent.max_tickets || 8,
                    product_type: selectedEvent.product_type || 'Экскурсия'
                })
            });
            const data = await response.json();

            if (!data.success) {
                alert(data.error || 'Не удалось сохранить настройки слота');
                return;
            }

            setSelectedEvent((prev) => ({
                ...prev,
                ...data.event
            }));

            if (selectedDayData?.date) {
                await fetchDayEvents(selectedDayData.date);
            }
        } catch (error) {
            console.error('Error saving event settings:', error);
            alert('Не удалось сохранить настройки слота');
        }
    };

    // Загрузка меню события
    const fetchEventMenu = async (eventId) => {
        try {
            const response = await apiFetch(`${API_URL}/api/menu/event/${eventId}`);
            const data = await response.json();

            setEventMenu(data.menu || []);
        } catch (error) {
            console.error('Error fetching event menu:', error);
        }
    };

    const fetchEventStaff = async (eventId) => {
        if (!token) {
            setEventStaff([]);
            return;
        }

        try {
            const response = await apiFetch(`${API_URL}/api/events/${eventId}/staff`, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });
            const data = await response.json();

            if (data.success) {
                const staff = data.staff || [];
                const selectedAdmin = dayAdmins.find((admin) => String(admin.id) === String(selectedDayAdminId));
                const mergedStaff = selectedAdmin && !staff.some((member) => String(member.employee_id) === String(selectedAdmin.id))
                    ? [{ employee_id: selectedAdmin.id, full_name: selectedAdmin.full_name, position: selectedAdmin.position || 'Администратор' }, ...staff]
                    : staff;

                setEventStaff(mergedStaff);
                setSalesDraft((prev) => ({
                    ...prev,
                    employee_id: prev.employee_id || (mergedStaff[0]?.employee_id ? String(mergedStaff[0].employee_id) : '')
                }));
            }
        } catch (error) {
            console.error('Error fetching event staff:', error);
            setEventStaff([]);
        }
    };

    const fetchEventSales = async (eventId) => {
        if (!token) {
            setEventSales([]);
            return;
        }

        try {
            const [salesResponse, souvenirResponse] = await Promise.all([
                apiFetch(`${API_URL}/api/sales-breakdown?event_id=${eventId}`, {
                    headers: { Authorization: `Bearer ${token}` }
                }),
                apiFetch(`${API_URL}/api/souvenirs/sales?event_id=${eventId}`, {
                    headers: { Authorization: `Bearer ${token}` }
                })
            ]);
            const [salesData, souvenirData] = await Promise.all([
                readJsonSafe(salesResponse),
                readJsonSafe(souvenirResponse)
            ]);

            // Пустой ответ/304 — оставляем то, что уже показано (не обнуляем список)
            if (!salesData) return;

            if (salesData.success) {
                const linesMap = new Map(
                    (souvenirData?.success ? souvenirData.data || [] : []).map((row) => [row.id, row.souvenir_lines || []])
                );
                setEventSales((salesData.data || []).map((sale) => ({
                    ...sale,
                    souvenir_lines: linesMap.get(sale.id) || []
                })));
            }
        } catch (error) {
            console.error('Error fetching event sales:', error);
        }
    };

    const fetchAvailableSouvenirItems = async () => {
        try {
            const response = await apiFetch(`${API_URL}/api/souvenirs/items?available=true`);
            const data = await response.json();
            setAvailableSouvenirItems(data.success ? (data.items || []) : []);
        } catch (error) {
            console.error('Error fetching souvenir items:', error);
            setAvailableSouvenirItems([]);
        }
    };

    const souvenirCartTotal = useMemo(
        () => souvenirCart.reduce((sum, row) => sum + Number(row.line_total || 0), 0),
        [souvenirCart]
    );

    const barCartTotals = useMemo(
        () => barCart.reduce((acc, row) => {
            const amount = Number(row.line_total || 0);
            if (row.destination === 'cafe') acc.cafe += amount;
            else acc.beforeAfter += amount;
            return acc;
        }, { cafe: 0, beforeAfter: 0 }),
        [barCart]
    );

    const handleAddBarToCart = () => {
        if (!selectedBarItemId) {
            alert('Выберите позицию бара');
            return;
        }
        const item = availableMenuItems.find((entry) => String(entry.id) === String(selectedBarItemId));
        if (!item) return;
        const quantity = Math.max(1, parseInt(barQty, 10) || 1);
        const unitPrice = Number(item.price || 0);
        const lineTotal = Math.round(unitPrice * quantity * 100) / 100;
        setBarCart((prev) => {
            const destination = barDestinationDraft === 'cafe' ? 'cafe' : 'before_after';
            const paymentMethod = barPaymentDraft || 'cash';
            const existingIndex = prev.findIndex(
                (row) => row.menu_item_id === item.id
                    && row.destination === destination
                    && (row.payment_method || 'cash') === paymentMethod
            );
            if (existingIndex >= 0) {
                const next = [...prev];
                const mergedQty = next[existingIndex].quantity + quantity;
                next[existingIndex] = {
                    ...next[existingIndex],
                    quantity: mergedQty,
                    line_total: Math.round(unitPrice * mergedQty * 100) / 100
                };
                return next;
            }
            return [...prev, {
                menu_item_id: item.id,
                name: item.name,
                destination,
                payment_method: paymentMethod,
                unit_price: unitPrice,
                quantity,
                line_total: lineTotal
            }];
        });
        setSelectedBarItemId('');
        setBarQty(1);
    };

    const handleRemoveBarFromCart = (menuItemId, destination, paymentMethod) => {
        setBarCart((prev) => prev.filter(
            (row) => !(row.menu_item_id === menuItemId
                && row.destination === destination
                && (row.payment_method || 'cash') === (paymentMethod || 'cash'))
        ));
    };

    const handleBarPaymentChange = (menuItemId, destination, paymentMethod, nextPaymentMethod) => {
        setBarCart((prev) => prev.map((row) => {
            if (row.menu_item_id !== menuItemId
                || row.destination !== destination
                || (row.payment_method || 'cash') !== (paymentMethod || 'cash')) {
                return row;
            }
            return { ...row, payment_method: nextPaymentMethod };
        }));
    };

    const handleBarDestinationChange = (menuItemId, destination, paymentMethod, nextDestination) => {
        setBarCart((prev) => prev.map((row) => {
            if (row.menu_item_id !== menuItemId
                || row.destination !== destination
                || (row.payment_method || 'cash') !== (paymentMethod || 'cash')) {
                return row;
            }
            return { ...row, destination: nextDestination };
        }));
    };

    const handleAddSouvenirToCart = () => {
        if (!selectedSouvenirId) {
            alert('Выберите сувенир');
            return;
        }

        const item = availableSouvenirItems.find((entry) => String(entry.id) === String(selectedSouvenirId));
        if (!item) return;

        const quantity = Math.max(1, parseInt(souvenirQty, 10) || 1);
        const unitPrice = Number(item.price || 0);
        const lineTotal = Math.round(unitPrice * quantity * 100) / 100;

        setSouvenirCart((prev) => {
            const paymentMethod = souvenirPaymentDraft || 'cash';
            const existingIndex = prev.findIndex(
                (row) => row.souvenir_item_id === item.id
                    && (row.payment_method || 'cash') === paymentMethod
            );
            if (existingIndex >= 0) {
                const next = [...prev];
                const mergedQty = next[existingIndex].quantity + quantity;
                next[existingIndex] = {
                    ...next[existingIndex],
                    quantity: mergedQty,
                    line_total: Math.round(unitPrice * mergedQty * 100) / 100
                };
                return next;
            }
            return [...prev, {
                souvenir_item_id: item.id,
                name: item.name,
                category: item.category,
                payment_method: paymentMethod,
                unit_price: unitPrice,
                quantity,
                line_total: lineTotal
            }];
        });

        setSelectedSouvenirId('');
        setSouvenirQty(1);
    };

    const handleRemoveSouvenirFromCart = (souvenirItemId, paymentMethod) => {
        setSouvenirCart((prev) => prev.filter(
            (row) => !(row.souvenir_item_id === souvenirItemId
                && (row.payment_method || 'cash') === (paymentMethod || 'cash'))
        ));
    };

    const handleSouvenirPaymentChange = (souvenirItemId, paymentMethod, nextPaymentMethod) => {
        setSouvenirCart((prev) => prev.map((row) => {
            if (row.souvenir_item_id !== souvenirItemId
                || (row.payment_method || 'cash') !== (paymentMethod || 'cash')) {
                return row;
            }
            return { ...row, payment_method: nextPaymentMethod };
        }));
    };

    const filteredMenuItemsForDialog = useMemo(() => {
        if (!menuCategoryFilter) {
            return availableMenuItems;
        }
        const filter = menuCategoryFilter.toLowerCase();
        return availableMenuItems.filter(
            (item) => (item.category || '').toLowerCase() === filter
        );
    }, [availableMenuItems, menuCategoryFilter]);

    // Загрузка доступных блюд
    const fetchAvailableMenuItems = async () => {
        try {
            const response = await apiFetch(`${API_URL}/api/menu/items?available=true`);
            const data = await response.json();

            setAvailableMenuItems(data.items || []);
        } catch (error) {
            console.error('Error fetching menu items:', error);
        }
    };

    const resetClientExtraDraft = (paymentMethod = 'cash') => {
        setClientExtraDraft({
            menu_item_id: '',
            quantity: 1,
            payment_method: paymentMethod || 'cash',
            notes: ''
        });
    };

    const resetVisitorExtraDraft = (paymentMethod = 'cash') => {
        setVisitorExtraDraft({
            menu_item_id: '',
            quantity: 1,
            payment_method: paymentMethod || 'cash',
            notes: ''
        });
    };

    const fetchClientExtras = async (orderId) => {
        if (!orderId) {
            setClientExtras([]);
            return;
        }

        try {
            const response = await apiFetch(`${API_URL}/api/yandex/order/${orderId}/extras`);
            const data = await response.json();
            const extras = data.success ? (data.extras || []) : [];
            setClientExtras(extras);
            return extras;
        } catch (error) {
            console.error('Error fetching client extras:', error);
            setClientExtras([]);
            return [];
        }
    };

    // Добавить блюдо к событию
    const handleAddMenuItem = async () => {
        if (!selectedMenuItem || !selectedEvent) return;

        try {
            const response = await apiFetch(`${API_URL}/api/menu/event/${selectedEvent.id}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    menu_item_id: selectedMenuItem,
                    quantity: menuQuantity
                })
            });

            const data = await response.json();

            if (data.success) {
                await fetchEventMenu(selectedEvent.id);
                setOpenAddMenuDialog(false);
                setSelectedMenuItem('');
                setMenuCategoryFilter('');
                setMenuQuantity(1);
            }
        } catch (error) {
            console.error('Error adding menu item:', error);
        }
    };

    // Удалить блюдо из меню события
    const handleRemoveMenuItem = async (itemId) => {
        if (!selectedEvent) return;

        try {
            const response = await apiFetch(`${API_URL}/api/menu/event/${selectedEvent.id}/item/${itemId}`, {
                method: 'DELETE'
            });

            const data = await response.json();

            if (data.success) {
                await fetchEventMenu(selectedEvent.id);
            }
        } catch (error) {
            console.error('Error removing menu item:', error);
        }
    };

    // Загрузить свободные билеты для события (используем данные из events)
    const fetchAvailableTickets = async (eventId) => {
        try {
            setAvailableTicketsError('');
            const response = await apiFetch(`${API_URL}/api/yandex/event/${eventId}/available-tickets`);
            const data = await response.json();

            if (!data.success) {
                setAvailableRates([]);
                setAvailableTicketsCount(0);
                setAvailableTicketsError(
                    data.error?.includes('401') || data.error?.includes('Invalid password')
                        ? 'Ошибка авторизации Agent API Яндекса. Обратитесь к администратору: проверьте пароль YANDEX_AGENT_PASSWORD в .env.'
                        : (data.error || 'Не удалось загрузить тарифы Яндекса')
                );
                return { rates: [], yandexAvailable: 0, tickets: [] };
            }

            const yandexAvailable = parseInt(data.total_available, 10) || 0;
            const rawRates = Array.isArray(data.rates)
                ? data.rates
                : Object.values(data.rates || {});
            const parsedRates = rawRates
                .map(rate => ({
                    ...rate,
                    id: rate.id ?? rate.rate_id ?? 0,
                    name: rate.name || rate.title || rate.rate_name,
                    price: parseFloat(rate.price || rate.cost || 0),
                    fromYandex: true
                }))
                .filter(rate => rate.name && rate.id != null);
            const rates = parsedRates.length ? parsedRates : [{
                id: 0,
                name: 'Взрослый билет',
                price: 1000,
                fromYandex: false
            }];

            setAvailableRates(rates);
            setAvailableTicketsCount(yandexAvailable);
            setAvailableTicketsError('');

            return { rates, yandexAvailable, tickets: data.tickets || [] };
        } catch (error) {
            console.error('Error fetching available tickets:', error);
            setAvailableRates([]);
            setAvailableTicketsCount(0);
            setAvailableTicketsError('Не удалось загрузить тарифы Яндекса');
            return { rates: [], yandexAvailable: 0, tickets: [] };
        }
    };

    // Открыть диалог добавления блюда
    const handleOpenAddMenuDialog = () => {
        fetchAvailableMenuItems();
        setMenuCategoryFilter('');
        setSelectedMenuItem('');
        setMenuQuantity(1);
        setOpenAddMenuDialog(true);
    };

    const bookingDisabledReason = bookingInProgress
        ? 'Идёт бронирование…'
        : !String(newVisitor.visitor_name || '').trim()
            ? 'Укажите имя посетителя'
            : '';

    // Добавить посетителя вручную
    const handleAddVisitor = async () => {
        if (!selectedEvent || !String(newVisitor.visitor_name || '').trim()) return;

        const defaultRate = pickDefaultYandexRate(availableRates, []);
        const ticketsToSend = ticketsList.map((ticket) => {
            if (ticket.rate_id != null && ticket.rate_id !== '') {
                return ticket;
            }
            return { ...ticket, ...ticketFromRate(defaultRate, ticket.quantity || 1) };
        });

        if (ticketsToSend.some((ticket) => ticket.rate_id == null || ticket.rate_id === '')) {
            alert('Выберите тип билета');
            return;
        }

        setBookingInProgress(true);
        try {
            const response = await apiFetch(`${API_URL}/api/yandex/event/${selectedEvent.id}/add-visitor`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    ...newVisitor,
                    createInYandex: true,
                    product_type: selectedEvent.product_type || 'Экскурсия',
                    tickets: ticketsToSend,
                    extras: visitorExtras.map((extra) => ({
                        item_type: extra.item_type || 'item',
                        menu_item_id: extra.menu_item_id,
                        bundle_id: extra.bundle_id || null,
                        quantity: extra.quantity || 1,
                        payment_method: visitorExtraDraft.payment_method || newVisitor.payment_method || 'cash',
                        notes: extra.notes || null
                    }))
                })
            });

            const data = await response.json();

            if (data.success) {
                await saveAwarenessOption(newVisitor.awareness_source);
                await fetchEventVisitors(selectedEvent.id);
                await fetchEventMenu(selectedEvent.id);
                await fetchAvailableTickets(selectedEvent.id);
                if (selectedDayData?.date) {
                    await fetchDayEvents(selectedDayData.date);
                }
                setOpenAddVisitorDialog(false);
                setNewVisitor({
                    visitor_name: '',
                    visitor_phone: '',
                    visitor_email: '',
                    payment_method: 'cash',
                    awareness_source: '',
                    visit_status: '',
                    payment_status: '',
                    notes: ''
                });
                setTicketsList([
                    { rate_name: '', price: 0, quantity: 1, sector: 'Входной билет' }
                ]);
                setVisitorExtras([]);
                resetVisitorExtraDraft(newVisitor.payment_method);
                if (data.extras_error) {
                    alert(`Посетитель записан, но блюда не все сохранились: ${data.extras_error}`);
                }
                if (data.slot_close_error) {
                    alert(`Посетитель записан, но остаток в Яндексе не закрылся: ${data.slot_close_error}`);
                }
            } else {
                alert(data.error || data.message || 'Не удалось забронировать в Яндексе');
            }
        } catch (error) {
            console.error('Error adding visitor:', error);
            alert('Ошибка при бронировании в Яндексе. Проверьте соединение и попробуйте снова.');
        } finally {
            setBookingInProgress(false);
        }
    };

    const handleOpenClientDialog = async (group) => {
        setClientDraft({
            order_id: group.order_id,
            visitor_name: group.visitor_name || '',
            visitor_phone: group.visitor_phone || '',
            visitor_email: group.visitor_email || '',
            payment_method: group.payment_method || 'cash',
            awareness_source: group.awareness_source || '',
            visit_status: group.visit_status || '',
            payment_status: group.payment_status || '',
            notes: group.notes || '',
            people_count: (group.tickets || []).length || 1,
            original_people_count: (group.tickets || []).length || 1,
            extra_payment_method: group.payment_method || 'cash'
        });
        resetClientExtraDraft(group.payment_method || 'cash');
        const extras = await Promise.all([
            fetchAvailableMenuItems(),
            fetchClientExtras(group.order_id)
        ]);
        const loadedExtras = extras[1];
        if (Array.isArray(loadedExtras) && loadedExtras[0]?.payment_method) {
            setClientDraft((prev) => ({ ...prev, extra_payment_method: loadedExtras[0].payment_method }));
        }
        setOpenClientDialog(true);
    };

    const handleAddClientExtra = async () => {
        if (!clientDraft.order_id) {
            return;
        }

        if (!clientExtraDraft.menu_item_id) {
            alert('Выберите позицию');
            return;
        }

        try {
            const response = await apiFetch(`${API_URL}/api/yandex/order/${clientDraft.order_id}/extras`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    item_type: 'item',
                    menu_item_id: Number(clientExtraDraft.menu_item_id),
                    bundle_id: null,
                    quantity: clientExtraDraft.quantity,
                    payment_method: clientDraft.extra_payment_method || clientDraft.payment_method || 'cash',
                    notes: clientExtraDraft.notes || null
                })
            });
            const data = await response.json();

            if (!data.success) {
                alert(data.error || 'Не удалось добавить позицию');
                return;
            }

            resetClientExtraDraft(clientDraft.payment_method);
            await fetchClientExtras(clientDraft.order_id);
            if (selectedEvent?.id) {
                await fetchEventVisitors(selectedEvent.id);
                await fetchEventMenu(selectedEvent.id);
            }
        } catch (error) {
            console.error('Error adding client extra:', error);
            alert('Не удалось добавить позицию');
        }
    };

    const handleChangeClientExtraQuantity = async (extra, nextQuantity) => {
        if (!clientDraft.order_id || nextQuantity < 1) {
            return;
        }

        try {
            const response = await apiFetch(`${API_URL}/api/yandex/order/${clientDraft.order_id}/extras/${extra.id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    quantity: nextQuantity,
                    notes: extra.notes || null
                })
            });
            const data = await response.json();

            if (!data.success) {
                alert(data.error || 'Не удалось обновить позицию');
                return;
            }

            await fetchClientExtras(clientDraft.order_id);
            if (selectedEvent?.id) {
                await fetchEventVisitors(selectedEvent.id);
                await fetchEventMenu(selectedEvent.id);
            }
        } catch (error) {
            console.error('Error updating client extra:', error);
            alert('Не удалось обновить позицию');
        }
    };

    const handleRemoveClientExtra = async (extraId) => {
        if (!clientDraft.order_id) {
            return;
        }

        try {
            const response = await apiFetch(`${API_URL}/api/yandex/order/${clientDraft.order_id}/extras/${extraId}`, {
                method: 'DELETE'
            });
            const data = await response.json();

            if (!data.success) {
                alert(data.error || 'Не удалось удалить позицию');
                return;
            }

            await fetchClientExtras(clientDraft.order_id);
            if (selectedEvent?.id) {
                await fetchEventVisitors(selectedEvent.id);
                await fetchEventMenu(selectedEvent.id);
            }
        } catch (error) {
            console.error('Error deleting client extra:', error);
            alert('Не удалось удалить позицию');
        }
    };

    const handleSaveClientDraft = async () => {
        if (!clientDraft.order_id) return;

        try {
            const payload = {
                visitor_name: clientDraft.visitor_name || null,
                visitor_phone: clientDraft.visitor_phone || null,
                visitor_email: clientDraft.visitor_email || null,
                payment_method: clientDraft.payment_method || null,
                awareness_source: clientDraft.awareness_source || null,
                visit_status: clientDraft.visit_status || null,
                payment_status: clientDraft.payment_status || null,
                notes: clientDraft.notes || null
            };
            const response = await apiFetch(`${API_URL}/api/yandex/order/${clientDraft.order_id}/client`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            const data = await response.json();
            if (!data.success) {
                alert(data.error || 'Ошибка сохранения данных клиента');
                return;
            }

            if (clientDraft.people_count && Number(clientDraft.people_count) !== Number(clientDraft.original_people_count || clientDraft.people_count)) {
                const isYandexSiteOrder = !String(clientDraft.order_id || '').startsWith('9');
                if (isYandexSiteOrder && !window.confirm('Это заявка из Яндекса. Изменить число человек и при необходимости аннулировать лишние билеты?')) {
                    return;
                }
                const peopleRes = await apiFetch(`${API_URL}/api/yandex/order/${clientDraft.order_id}/people`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ quantity: Number(clientDraft.people_count) || 1 })
                });
                const peopleData = await peopleRes.json();
                if (!peopleData.success) {
                    alert(peopleData.error || 'Не удалось изменить число человек');
                    return;
                }
            }

            if (clientDraft.extra_payment_method) {
                await apiFetch(`${API_URL}/api/yandex/order/${clientDraft.order_id}/extras-payment`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ payment_method: clientDraft.extra_payment_method })
                });
            }

            setOpenClientDialog(false);
            await saveAwarenessOption(clientDraft.awareness_source);
            await fetchEventVisitors(selectedEvent.id);
        } catch (error) {
            console.error('Error saving client fields:', error);
            alert('Ошибка сохранения данных клиента');
        }
    };

    const handleSaveEventSales = async () => {
        if (!token || !selectedEvent) {
            alert('Нужен вход в систему');
            return;
        }

        if (!salesDraft.employee_id) {
            alert('Сначала назначьте сотрудника на слот');
            return;
        }

        const hasSouvenirs = souvenirCart.length > 0;
        const hasBar = barCart.length > 0;

        if (!hasSouvenirs && !hasBar) {
            alert('Добавьте сувениры или позиции бара');
            return;
        }

        if (salesSaving) {
            return;
        }
        setSalesSaving(true);

        try {
            const saleDate = (selectedEvent.event_date || '').slice(0, 10);
            const employeeId = parseInt(salesDraft.employee_id, 10);
            const requests = [];

            // Группируем сувениры по способу оплаты: на каждую группу — отдельная продажа
            const souvenirGroups = new Map();
            souvenirCart.forEach((row) => {
                const method = row.payment_method || 'cash';
                if (!souvenirGroups.has(method)) souvenirGroups.set(method, []);
                souvenirGroups.get(method).push({
                    souvenir_item_id: row.souvenir_item_id,
                    quantity: row.quantity
                });
            });

            // Бар: строки храним целиком, чтобы посчитать суммы по назначению (кафе / до-после)
            const barRowsByMethod = new Map();
            barCart.forEach((row) => {
                const method = row.payment_method || 'cash';
                if (!barRowsByMethod.has(method)) barRowsByMethod.set(method, []);
                barRowsByMethod.get(method).push(row);
            });

            const barAmounts = (rows) => rows.reduce((acc, row) => {
                const amount = Number(row.line_total || 0);
                if (row.destination === 'cafe') acc.cafe += amount;
                else acc.beforeAfter += amount;
                return acc;
            }, { cafe: 0, beforeAfter: 0 });

            const barItemsPayload = (rows) => rows.map((row) => ({
                menu_item_id: row.menu_item_id,
                quantity: row.quantity,
                destination: row.destination || 'before_after'
            }));

            const postSale = (url, body) => apiFetch(`${API_URL}${url}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify(body)
            });

            // Способы оплаты, по которым есть сувениры: сувениры и бар сохраняем одной продажей
            souvenirGroups.forEach((items, method) => {
                const barRows = barRowsByMethod.get(method) || [];
                barRowsByMethod.delete(method);
                const amounts = barAmounts(barRows);
                requests.push(postSale('/api/souvenirs/sales', {
                    employee_id: employeeId,
                    event_id: selectedEvent.id,
                    sale_date: saleDate,
                    tickets_amount: 0,
                    cafe_amount: amounts.cafe,
                    bar_before_after_amount: amounts.beforeAfter,
                    payment_method: method,
                    notes: salesDraft.notes || null,
                    items,
                    bar_items: barItemsPayload(barRows)
                }));
            });

            // Остальные способы оплаты — только бар
            barRowsByMethod.forEach((barRows, method) => {
                const amounts = barAmounts(barRows);
                requests.push(postSale('/api/sales-breakdown', {
                    employee_id: employeeId,
                    event_id: selectedEvent.id,
                    sale_date: saleDate,
                    tickets_amount: 0,
                    souvenirs_amount: 0,
                    cafe_amount: amounts.cafe,
                    bar_before_after_amount: amounts.beforeAfter,
                    payment_method: method,
                    notes: salesDraft.notes || null,
                    bar_items: barItemsPayload(barRows)
                }));
            });

            // Сначала дожидаемся ответов, и только потом читаем тела
            // (requests — массив промисов, у промиса нет метода text()).
            const responses = await Promise.all(requests);
            const payloads = await Promise.all(responses.map((response) => readJsonSafe(response)));

            const failureIndex = responses.findIndex((response, i) => (
                !response.ok || (payloads[i] && payloads[i].success === false)
            ));
            if (failureIndex !== -1) {
                const response = responses[failureIndex];
                const payload = payloads[failureIndex];
                const serverMessage = payload?.error
                    || payload?.message
                    || `HTTP ${response.status}, пустой ответ сервера`;
                alert(`Не удалось сохранить выручку: ${serverMessage}`);
                return;
            }

            await fetchEventSales(selectedEvent.id);
            setSouvenirCart([]);
            setBarCart([]);
            setSouvenirPaymentDraft('cash');
            setBarPaymentDraft('cash');
            setBarDestinationDraft('before_after');
            setSalesDraft((prev) => ({
                ...prev,
                notes: ''
            }));
        } catch (error) {
            console.error('Error saving event sales:', error);
            alert(`Ошибка сохранения выручки: ${error?.message || 'неизвестная ошибка'}`);
        } finally {
            setSalesSaving(false);
        }
    };

    const handleDeleteSouvenirLine = async (sale, line) => {
        if (!sale?.id || !line?.id) return;
        try {
            const response = await apiFetch(`${API_URL}/api/souvenirs/sales/${sale.id}/lines/${line.id}`, {
                method: 'DELETE'
            });
            const data = await response.json();
            if (!data.success) {
                alert(data.error || 'Не удалось удалить сувенир');
                return;
            }
            await fetchEventSales(selectedEvent.id);
        } catch (error) {
            console.error('Error deleting souvenir line:', error);
            alert('Не удалось удалить сувенир');
        }
    };

    // Открыть диалог удаления билета
    const handleOpenDeleteDialog = (visitor) => {
        setTicketToDelete(visitor);
        setOpenDeleteDialog(true);
    };

    // Удалить посетителя (билет) с выбором: аннулировать в Яндексе или нет
    const handleDeleteVisitor = async (cancelInYandex) => {
        if (!ticketToDelete) return;

        try {
            const response = await apiFetch(`${API_URL}/api/yandex/ticket/${ticketToDelete.id}`, {
                method: 'DELETE',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ cancelInYandex })
            });

            const data = await response.json();

            if (data.success) {
                // Обновляем список посетителей
                await fetchEventVisitors(selectedEvent.id);
                setOpenDeleteDialog(false);
                setTicketToDelete(null);
            } else {
                alert(`Ошибка: ${data.error}`);
            }
        } catch (error) {
            console.error('Error deleting visitor:', error);
            alert('Ошибка при удалении билета');
        }
    };

    // Показать информацию о билете (barcode)
    const handleShowTicketInfo = async (ticketId, orderId) => {
        // Проверка на ручной билет
        if (!orderId || orderId.toString().startsWith('9')) {
            alert('Этот билет был добавлен вручную');
            return;
        }

        try {
            const response = await apiFetch(`${API_URL}/api/yandex/ticket/${ticketId}/info`);
            const data = await response.json();

            if (!data.success) {
                alert(data.error || 'Ошибка получения информации о билете');
                return;
            }

            const ticket = data.ticket;
            alert(
                `📋 Информация о билете\n\n` +
                `👤 Посетитель: ${ticket.visitor_name || '-'}\n` +
                `📱 Телефон: ${ticket.visitor_phone || '-'}\n` +
                `✉️ Email: ${ticket.visitor_email || '-'}\n` +
                `💰 Цена: ₽${ticket.price}\n` +
                `🎫 Тариф: ${ticket.rate_name}\n` +
                `📍 Сектор: ${ticket.sector}\n` +
                `🔢 Штрихкод: ${ticket.barcode}\n\n` +
                `ℹ️ Agent API недоступен для данного аккаунта.\n` +
                `Для скачивания PDF обратитесь в поддержку Яндекс.Билеты.`
            );
        } catch (error) {
            console.error('Error fetching ticket info:', error);
            alert('Ошибка получения информации о билете');
        }
    };

    useEffect(() => {
        fetchCalendarData();
        fetchStats();
        fetchVisits();
        fetchSources();
        fetchAwarenessOptions();
        fetchPriceList();
        fetchPaymentOptions();
        loadDayAdmins();
    }, [currentDate]);

    useEffect(() => {
        if (openDayDialog && selectedDayData?.date) {
            fetchDayEvents(selectedDayData.date);
        }
    }, [showHiddenSlots]);

    // Навигация по месяцам
    const previousMonth = () => {
        setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
    };

    const nextMonth = () => {
        setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
    };

    // Получение дней месяца
    const getDaysInMonth = () => {
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth();
        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);

        const days = [];
        const startDayOfWeek = firstDay.getDay() === 0 ? 6 : firstDay.getDay() - 1; // Понедельник = 0

        // Добавляем пустые ячейки для выравнивания
        for (let i = 0; i < startDayOfWeek; i++) {
            days.push(null);
        }

        // Добавляем дни месяца
        for (let day = 1; day <= lastDay.getDate(); day++) {
            days.push(day);
        }

        return days;
    };

    // Получение данных для конкретного дня
    const getDayData = (day) => {
        if (!day) return null;

        const dateStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
        const result = calendarData.find(item => item.date === dateStr);

        // Логирование для первого дня (для отладки)
        if (day === 1) {
            console.log('🔍 getDayData for day 1:', dateStr);
            console.log('📊 calendarData length:', calendarData.length);
            console.log('🎯 Found:', result);
            if (calendarData.length > 0) {
                console.log('📅 First item in calendarData:', calendarData[0]);
                console.log('📅 Sample dates from calendarData:', calendarData.slice(0, 5).map(item => item.date));
            }
        }

        return result;
    };

    // Обработка добавления посещения
    const handleAddVisit = async () => {
        try {
            const response = await apiFetch(`${API_URL}/api/visits`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(newVisit),
            });

            const data = await response.json();

            if (data.success) {
                setOpenDialog(false);
                fetchCalendarData();
                fetchStats();
                fetchVisits();

                // Сброс формы
                setNewVisit({
                    visit_date: new Date().toISOString().split('T')[0],
                    source_name: 'yandex',
                    visitor_name: '',
                    visitor_phone: '',
                    visitor_email: '',
                    tickets_count: 1,
                    total_amount: 0,
                    notes: '',
                    awareness_source: ''
                });
            }
        } catch (error) {
            console.error('Error adding visit:', error);
        }
    };

    const monthNames = [
        'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
        'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'
    ];

    const formatDayTitle = (dateStr) => {
        if (!dateStr) return 'Слоты дня';
        const date = new Date(`${String(dateStr).slice(0, 10)}T12:00:00`);
        if (Number.isNaN(date.getTime())) return 'Слоты дня';
        const label = date.toLocaleDateString('ru-RU', {
            weekday: 'long',
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        });
        return label.charAt(0).toUpperCase() + label.slice(1);
    };

    const weekDays = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];

    // Подсчет общей статистики
    const totalVisits = stats.reduce((sum, item) => sum + parseInt(item.visits_count || 0), 0);
    const totalTickets = stats.reduce((sum, item) => sum + parseInt(item.total_tickets || 0), 0);
    const totalAmount = stats.reduce((sum, item) => sum + parseFloat(item.total_amount || 0), 0);

    if (showReport) {
        return (
            <Box sx={{ p: 3 }}>
                <Box sx={{ mb: 3 }}>
                    <Button
                        variant="outlined"
                        onClick={() => setShowReport(false)}
                    >
                        ← Назад к календарю
                    </Button>
                </Box>
                <VisitsReportPanel token={token} />
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                    Посещения музея
                </Typography>
                <Box display="flex" gap={1}>
                    <Button
                        variant="outlined"
                        onClick={() => setShowReport(true)}
                    >
                        📊 Отчет
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => setOpenDialog(true)}
                        sx={{
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            '&:hover': {
                                background: 'linear-gradient(135deg, #5568d3 0%, #63408b 100%)',
                            }
                        }}
                    >
                        Добавить посещение
                    </Button>
                </Box>
            </Box>

            {/* Календарь */}
            <Paper sx={{ p: 3, mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, gap: 2, flexWrap: 'wrap' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <IconButton onClick={previousMonth}>
                            <ChevronLeft />
                        </IconButton>
                        <Typography variant="h5" sx={{ fontWeight: 'bold', mx: 1 }}>
                            {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
                        </Typography>
                        <IconButton onClick={nextMonth}>
                            <ChevronRight />
                        </IconButton>
                    </Box>
                    {canSync && (
                        <Button
                            variant="outlined"
                            size="small"
                            disabled={syncingMonth}
                            onClick={handleSyncCurrentMonth}
                        >
                            {syncingMonth ? 'Синхронизация...' : 'Синхронизировать месяц с Яндексом'}
                        </Button>
                    )}
                </Box>
                {syncMonthMsg && (
                    <Typography variant="body2" sx={{ mb: 2, color: syncMonthMsg.startsWith('✅') ? 'success.main' : 'error.main' }}>
                        {syncMonthMsg}
                    </Typography>
                )}

                {/* Заголовки дней недели */}
                <Grid container spacing={1} sx={{ mb: 1 }}>
                    {weekDays.map((day) => (
                        <Grid item xs key={day}>
                            <Box sx={{ textAlign: 'center', fontWeight: 'bold', py: 1 }}>
                                {day}
                            </Box>
                        </Grid>
                    ))}
                </Grid>

                {/* Дни месяца */}
                <Grid container spacing={1}>
                    {getDaysInMonth().map((day, index) => {
                        const dayData = getDayData(day);

                        return (
                            <Grid item xs key={index}>
                                <Box
                                    onClick={() => handleDayClick(day)}
                                    sx={{
                                        minHeight: 100,
                                        border: '1px solid #e0e0e0',
                                        borderRadius: 1,
                                        p: 1,
                                        bgcolor: day ? '#fff' : '#f5f5f5',
                                        cursor: day ? 'pointer' : 'default',
                                        transition: 'all 0.2s',
                                        '&:hover': day ? {
                                            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                            transform: 'translateY(-2px)',
                                        } : {},
                                    }}
                                >
                                    {day && (
                                        <>
                                            <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.25 }}>
                                                {day}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5, textTransform: 'lowercase' }}>
                                                {weekDays[(new Date(currentDate.getFullYear(), currentDate.getMonth(), day).getDay() + 6) % 7]}
                                            </Typography>
                                            {dayData ? (
                                                <Box>
                                                    <Typography variant="caption" display="block" sx={{ fontWeight: 600 }}>
                                                        🎫 {dayData.total_tickets || 0} билетов
                                                    </Typography>
                                                    <Typography variant="caption" display="block" sx={{ mt: 0.5, color: 'success.main', fontWeight: 600 }}>
                                                        ₽{Number(dayData.total_amount || 0).toLocaleString('ru-RU')}
                                                    </Typography>
                                                </Box>
                                            ) : (
                                                <Typography variant="caption" color="text.secondary">
                                                    Открыть слоты дня
                                                </Typography>
                                            )}
                                        </>
                                    )}
                                </Box>
                            </Grid>
                        );
                    })}
                </Grid>
            </Paper>

            {/* Таблица последних посещений */}
            <Paper sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                    Последние посещения
                </Typography>
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                                <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Дата</TableCell>
                                <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Источник</TableCell>
                                <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Посетитель</TableCell>
                                <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Телефон</TableCell>
                                <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Email</TableCell>
                                <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Билетов</TableCell>
                                <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Сумма</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {visits.slice(0, 10).map((visit) => (
                                <TableRow key={visit.id} hover>
                                    <TableCell>
                                        {new Date(visit.visit_date).toLocaleDateString('ru-RU')}
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={getVisitSourceDisplay(visit.source_name)}
                                            size="small"
                                            sx={{
                                                bgcolor: visit.source_color,
                                                color: '#fff',
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell>{visit.visitor_name || '-'}</TableCell>
                                    <TableCell>{visit.visitor_phone || '-'}</TableCell>
                                    <TableCell>{visit.visitor_email || '-'}</TableCell>
                                    <TableCell>{visit.tickets_count}</TableCell>
                                    <TableCell>₽{parseFloat(visit.total_amount).toLocaleString('ru-RU')}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            {/* Диалог добавления посещения */}
            <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Добавить посещение</DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <TextField
                            label="Дата посещения"
                            type="date"
                            value={newVisit.visit_date}
                            onChange={(e) => setNewVisit({ ...newVisit, visit_date: e.target.value })}
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                        />

                        <FormControl fullWidth>
                            <InputLabel>Источник</InputLabel>
                            <Select
                                value={newVisit.source_name}
                                label="Источник"
                                onChange={(e) => setNewVisit({ ...newVisit, source_name: e.target.value })}
                            >
                                {sources.map((source) => (
                                    <MenuItem key={source.id} value={source.name}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Box
                                                sx={{
                                                    width: 16,
                                                    height: 16,
                                                    borderRadius: '50%',
                                                    bgcolor: source.color,
                                                }}
                                            />
                                            {getVisitSourceDisplay(source)}
                                        </Box>
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <Autocomplete
                            freeSolo
                            options={awarenessOptions}
                            value={getAwarenessSourceDisplay(newVisit.awareness_source) || null}
                            onChange={(_, newValue) => setNewVisit({ ...newVisit, awareness_source: newValue || '' })}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Откуда узнали"
                                    placeholder="Выберите или введите свой источник"
                                />
                            )}
                        />

                        <TextField
                            label="Имя посетителя"
                            value={newVisit.visitor_name}
                            onChange={(e) => setNewVisit({ ...newVisit, visitor_name: e.target.value })}
                            fullWidth
                        />

                        <TextField
                            label="Телефон"
                            value={newVisit.visitor_phone}
                            onChange={(e) => setNewVisit({ ...newVisit, visitor_phone: e.target.value })}
                            fullWidth
                        />

                        <TextField
                            label="Email"
                            type="email"
                            value={newVisit.visitor_email}
                            onChange={(e) => setNewVisit({ ...newVisit, visitor_email: e.target.value })}
                            fullWidth
                        />

                        <TextField
                            label="Количество билетов"
                            type="number"
                            value={newVisit.tickets_count}
                            onChange={(e) => setNewVisit({ ...newVisit, tickets_count: parseInt(e.target.value) })}
                            fullWidth
                        />

                        <TextField
                            label="Сумма"
                            type="number"
                            value={newVisit.total_amount}
                            onChange={(e) => setNewVisit({ ...newVisit, total_amount: parseFloat(e.target.value) })}
                            fullWidth
                        />

                        <TextField
                            label="Примечания"
                            value={newVisit.notes}
                            onChange={(e) => setNewVisit({ ...newVisit, notes: e.target.value })}
                            multiline
                            rows={3}
                            fullWidth
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDialog(false)}>Отмена</Button>
                    <Button
                        onClick={handleAddVisit}
                        variant="contained"
                        sx={{
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        }}
                    >
                        Добавить
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Диалог просмотра слотов за день */}
            <Dialog
                open={openDayDialog}
                onClose={() => setOpenDayDialog(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                        <Typography variant="h6" component="div" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
                            {formatDayTitle(selectedDayData?.date)}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                            <FormControl size="small" sx={{ minWidth: 240 }}>
                                <InputLabel>Администратор дня</InputLabel>
                                <Select
                                    value={selectedDayAdminId}
                                    label="Администратор дня"
                                    onChange={(e) => handleDayAdminChange(e.target.value)}
                                >
                                    <MenuItem value="">
                                        <em>Не назначен</em>
                                    </MenuItem>
                                    {dayAdmins.map((admin) => (
                                        <MenuItem key={admin.id} value={String(admin.id)}>
                                            {admin.full_name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <Chip
                                label={`${selectedDayData?.total_tickets || 0} билетов`}
                                color="success"
                            />
                            <Button
                                size="small"
                                variant={showHiddenSlots ? 'contained' : 'outlined'}
                                onClick={() => setShowHiddenSlots((prev) => !prev)}
                            >
                                {showHiddenSlots ? 'Скрытые показаны' : 'Показать скрытые'}
                            </Button>
                        </Box>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    {/* Список слотов по времени */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {dayEvents.map((event) => {
                            // Используем UTC время напрямую, без конвертации в локальное
                            const date = new Date(event.event_date);
                            const eventTime = `${String(date.getUTCHours()).padStart(2, '0')}:${String(date.getUTCMinutes()).padStart(2, '0')}`;
                            const saleState = getYandexSaleState(event);

                            return (
                                <Card
                                    key={event.id}
                                    sx={{
                                        cursor: 'pointer',
                                        bgcolor: saleState.open ? '#fff' : '#f8fafc',
                                        opacity: saleState.open ? 1 : 0.78,
                                        '&:hover': { bgcolor: saleState.open ? '#f5f5f5' : '#eef2f7' }
                                    }}
                                    onClick={() => handleEventClick(event)}
                                >
                                    <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <Box sx={{ flex: 1 }}>
                                                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                                                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                                        {eventTime} - {event.name}
                                                    </Typography>
                                                    {(Array.isArray(event.staff) ? event.staff : []).map((member) => (
                                                        <Chip
                                                            key={member.employee_id}
                                                            label={member.full_name}
                                                            size="small"
                                                            sx={{
                                                                bgcolor: /гид/i.test(member.position || '') ? '#ecfdf5' : '#eff6ff',
                                                                color: /гид/i.test(member.position || '') ? '#047857' : '#1d4ed8',
                                                                fontWeight: 700,
                                                                height: 22,
                                                            }}
                                                        />
                                                    ))}
                                                    {!(event.staff && event.staff.length) && (
                                                        <Chip
                                                            label="Гид не назначен"
                                                            size="small"
                                                            variant="outlined"
                                                            sx={{ height: 22, color: '#94a3b8', borderColor: '#cbd5e1' }}
                                                        />
                                                    )}
                                                    <Chip
                                                        label={event.product_type || 'Экскурсия'}
                                                        size="small"
                                                        color="primary"
                                                        variant="outlined"
                                                    />
                                                    {event.is_archived && (
                                                        <Chip label="Скрыт в CRM" size="small" color="warning" />
                                                    )}
                                                </Box>
                                                <Box sx={{ display: 'flex', gap: 2, mt: 0.5, flexWrap: 'wrap' }}>
                                                    {event.tickets_sold > 0 && (
                                                        <Typography variant="body2" color="primary" sx={{ fontWeight: 'bold' }}>
                                                            🎫 Продано: {event.tickets_sold}
                                                        </Typography>
                                                    )}
                                                    {Number.isFinite(Number(event.live_free)) ? (
                                                        <>
                                                            <Typography
                                                                variant="body2"
                                                                sx={{ fontWeight: 'bold', color: Number(event.live_free) > 0 ? 'success.main' : 'error.main' }}
                                                            >
                                                                {Number(event.live_free) > 0 ? '✅' : '🚫'} Свободно в Яндексе: {event.live_free}
                                                            </Typography>
                                                            {Number(event.live_total) > Number(event.max_tickets || 8) && (
                                                                <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'error.main' }}>
                                                                    ⚠️ Открыто {event.live_total} при лимите {event.max_tickets}
                                                                </Typography>
                                                            )}
                                                        </>
                                                    ) : (event.live_error || saleState.label === 'Нет данных') ? (
                                                        <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'text.secondary' }}>
                                                            ⚠️ Нет данных из Яндекса
                                                        </Typography>
                                                    ) : (
                                                        <>
                                                            {saleState.open && event.available_tickets > 0 && (
                                                                <Typography variant="body2" color="success.main" sx={{ fontWeight: 'bold' }}>
                                                                    ≈ Свободно: {event.available_tickets}
                                                                </Typography>
                                                            )}
                                                            {!saleState.open && (
                                                                <Typography variant="body2" color="error.main" sx={{ fontWeight: 'bold' }}>
                                                                    В Яндексе закрыт
                                                                </Typography>
                                                            )}
                                                        </>
                                                    )}
                                                    {event.total_revenue > 0 && (
                                                        <Typography variant="body2" color="warning.main" sx={{ fontWeight: 'bold' }}>
                                                            💰 {Number(event.total_revenue).toLocaleString('ru-RU')} ₽
                                                        </Typography>
                                                    )}
                                                    {Number(event.menu_total || 0) > 0 && (
                                                        <Typography variant="body2" color="secondary.main" sx={{ fontWeight: 'bold' }}>
                                                            🍽️ Меню: {Number(event.menu_total).toLocaleString('ru-RU')} ₽
                                                        </Typography>
                                                    )}
                                                    {Number(event.order_extras_total || 0) > 0 && (
                                                        <Typography variant="body2" color="info.main" sx={{ fontWeight: 'bold' }}>
                                                            🧾 Клиенты: {Number(event.order_extras_total).toLocaleString('ru-RU')} ₽
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </Box>
                                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                                                <Chip
                                                    label={saleState.label}
                                                    color={saleState.color}
                                                    size="small"
                                                />
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    color="success"
                                                    title={slotHasCloseTickets(event)
                                                        ? 'Вернуть в продажу места, закрытые CRM'
                                                        : 'В CRM нет служебного закрытия слота — эти места уже в продаже в Яндексе'}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setSelectedEvent(event);
                                                        setOpenSeatsCount(1);
                                                        setOpenSeatsDialog(true);
                                                    }}
                                                >
                                                    ➕ В продажу
                                                </Button>
                                                <Button
                                                    size="small"
                                                    color="warning"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleSlotVisibilityChange(event.id, !event.is_archived);
                                                    }}
                                                >
                                                    {event.is_archived ? 'Вернуть в CRM' : 'Скрыть в CRM'}
                                                </Button>
                                            </Box>
                                        </Box>
                                    </CardContent>
                                </Card>
                            );
                        })}
                    </Box>

                    {dayEvents.length === 0 && (
                        <Box sx={{ textAlign: 'center', py: 3 }}>
                            <Typography color="text.secondary">
                                Нет слотов за этот день
                            </Typography>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDayDialog(false)}>Закрыть</Button>
                </DialogActions>
            </Dialog>

            {/* Диалог управления событием */}
            <Dialog
                open={openEventDialog}
                onClose={() => setOpenEventDialog(false)}
                maxWidth="lg"
                fullWidth
            >
                <DialogTitle>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box>
                            <Typography variant="h6">
                                {selectedEvent?.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {selectedEvent && (() => {
                                    const date = new Date(selectedEvent.event_date);
                                    const day = String(date.getUTCDate()).padStart(2, '0');
                                    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
                                    const year = date.getUTCFullYear();
                                    const hours = String(date.getUTCHours()).padStart(2, '0');
                                    const minutes = String(date.getUTCMinutes()).padStart(2, '0');
                                    return `${day}.${month}.${year}, ${hours}:${minutes}`;
                                })()}
                            </Typography>
                            {(eventStaff.length > 0 || (selectedEvent?.staff || []).length > 0) && (
                                <Box sx={{ display: 'flex', gap: 0.5, mt: 0.75, flexWrap: 'wrap' }}>
                                    {(eventStaff.length ? eventStaff : selectedEvent.staff).map((member) => (
                                        <Chip
                                            key={member.employee_id}
                                            size="small"
                                            label={member.full_name}
                                            sx={{
                                                bgcolor: /гид/i.test(member.position || '') ? '#ecfdf5' : '#eff6ff',
                                                color: /гид/i.test(member.position || '') ? '#047857' : '#1d4ed8',
                                                fontWeight: 700,
                                            }}
                                        />
                                    ))}
                                </Box>
                            )}
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                            <Chip
                                label={getYandexSaleState(selectedEvent, availableTicketsCount).label}
                                color={getYandexSaleState(selectedEvent, availableTicketsCount).color}
                                size="small"
                            />
                            {availableTicketsCount > 0 ? (
                                <Chip
                                    label={`Свободно: ${availableTicketsCount}`}
                                    color="success"
                                    variant="outlined"
                                />
                            ) : (
                                <Chip
                                    label="В Яндексе закрыт"
                                    color="error"
                                    variant="outlined"
                                />
                            )}
                            <Chip
                                label={`Продано: ${eventVisitors.length}`}
                                color="primary"
                            />
                            {Number(selectedEvent?.live_total) > Number(selectedEvent?.max_tickets || 8) && (
                                <Chip
                                    size="small"
                                    color="warning"
                                    label={`⚠️ В Яндексе открыто ${selectedEvent.live_total} при лимите ${selectedEvent.max_tickets || 8}`}
                                />
                            )}
                            <Button
                                size="small"
                                variant="outlined"
                                color={availableTicketsCount > 0 ? 'error' : 'success'}
                                disabled={yandexSaleBusy || !token}
                                onClick={() => handleYandexSale(availableTicketsCount > 0 ? 'close' : 'open')}
                            >
                                {yandexSaleBusy
                                    ? 'Секунду…'
                                    : availableTicketsCount > 0
                                        ? 'Закрыть в Яндексе'
                                        : 'Открыть в Яндексе'}
                            </Button>
                            <Button
                                size="small"
                                variant="outlined"
                                color="success"
                                disabled={!token}
                                title={slotHasCloseTickets(selectedEvent)
                                    ? 'Вернуть в продажу места, закрытые CRM'
                                    : 'В CRM нет служебного закрытия слота — эти места уже в продаже в Яндексе'}
                                onClick={() => {
                                    setOpenSeatsCount(1);
                                    setOpenSeatsDialog(true);
                                }}
                            >
                                ➕ Добавить билеты в продажу
                            </Button>
                        </Box>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <Paper variant="outlined" sx={{ p: 2 }}>
                                <Grid container spacing={2} alignItems="center">
                                    <Grid item xs={12} md={5}>
                                        <FormControl fullWidth size="small">
                                            <InputLabel>Тип продукта</InputLabel>
                                            <Select
                                                value={selectedEvent?.product_type || 'Экскурсия'}
                                                label="Тип продукта"
                                                onChange={async (e) => {
                                                    const product_type = e.target.value;
                                                    const max_tickets = getDefaultCapacityForProduct(product_type);
                                                    setSelectedEvent((prev) => ({
                                                        ...prev,
                                                        product_type,
                                                        max_tickets
                                                    }));
                                                    try {
                                                        await apiFetch(`${API_URL}/api/yandex/event/${selectedEvent.id}/settings`, {
                                                            method: 'PUT',
                                                            headers: { 'Content-Type': 'application/json' },
                                                            body: JSON.stringify({ max_tickets, product_type })
                                                        });
                                                    } catch (error) {
                                                        console.error('Error auto-saving product type:', error);
                                                    }
                                                }}
                                            >
                                                {PRODUCT_TYPE_OPTIONS.map((option) => (
                                                    <MenuItem key={option} value={option}>
                                                        {option}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                            {isPackageProductType(selectedEvent?.product_type) ? (
                                                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: 'block' }}>
                                                    {String(selectedEvent?.product_type || '').toLowerCase().includes('тренинг')
                                                        ? 'Тренинг: цену за всю запись ставите вручную в форме бронирования. Остаток слота закроется в Яндексе.'
                                                        : getSlotCatalogPrice(priceList, selectedEvent?.product_type, selectedEvent?.event_date) > 0
                                                            ? `Из прайса (${getDayKind(selectedEvent?.event_date) === 'weekend' ? 'выходные' : 'будни'}): ${getSlotCatalogPrice(priceList, selectedEvent?.product_type, selectedEvent?.event_date).toLocaleString('ru-RU')} ₽ за всю запись. Меняется в «Настройки CRM».`
                                                            : 'Фиксированная сумма за слот. Проставьте её в «Настройки CRM».'}
                                                </Typography>
                                            ) : (
                                                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: 'block' }}>
                                                    Билеты по прайсу: взрослый, детский, студенческий. Будни и выходные — разные цены. Студенческий только в будни до 18:00.
                                                </Typography>
                                            )}
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12} md={3}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            type="number"
                                            label="Вместимость CRM"
                                            value={selectedEvent?.max_tickets || 8}
                                            onChange={(e) => setSelectedEvent((prev) => ({
                                                ...prev,
                                                max_tickets: Number(e.target.value) || 8
                                            }))}
                                            inputProps={{ min: 1 }}
                                            helperText="Сколько людей допускаем в этот формат"
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <Box sx={{ display: 'flex', justifyContent: { xs: 'stretch', md: 'flex-end' }, gap: 1, flexWrap: 'wrap' }}>
                                            <Chip
                                                label={`Доступно в Яндексе: ${availableTicketsCount || 0}`}
                                                color={availableTicketsCount > 0 ? 'success' : 'error'}
                                                variant="outlined"
                                            />
                                            <Button variant="contained" onClick={handleSaveEventSettings} fullWidth>
                                                Сохранить настройки слота
                                            </Button>
                                        </Box>
                                    </Grid>
                                </Grid>
                            </Paper>
                        </Grid>

                        {/* Левая колонка - Посетители */}
                        <Grid item xs={12} md={7}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                <Typography variant="h6">
                                    Посетители
                                </Typography>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<AddIcon />}
                                    disabled={!getYandexSaleState(selectedEvent, availableTicketsCount).open}
                                    onClick={async () => {
                                        let loaded = { rates: availableRates, tickets: [] };
                                        if (selectedEvent?.id) {
                                            loaded = await fetchAvailableTickets(selectedEvent.id) || loaded;
                                        }
                                        const prices = await fetchPriceList();
                                        setTicketsList([
                                            defaultBookingTicket(
                                                selectedEvent?.product_type,
                                                prices.length ? prices : priceList,
                                                selectedEvent?.event_date,
                                                loaded.rates || []
                                            )
                                        ]);
                                        setVisitorExtras([]);
                                        resetVisitorExtraDraft(newVisitor.payment_method || 'cash');
                                        if (!availableMenuItems.length) {
                                            await fetchAvailableMenuItems();
                                        }
                                        setOpenAddVisitorDialog(true);
                                    }}
                                >
                                    Добавить посетителя
                                </Button>
                            </Box>
                            {!getYandexSaleState(selectedEvent, availableTicketsCount).open && (
                                <Typography variant="caption" color="error.main" sx={{ display: 'block', mb: 1 }}>
                                    Слот закрыт в Яндексе — сначала откройте продажу, затем записывайте гостей.
                                </Typography>
                            )}
                            <TableContainer component={Paper} variant="outlined">
                                <Table size="small">
                                    <TableHead>
                                        <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                                            <TableCell sx={{ fontWeight: 'bold' }}>Имя</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold' }}>Телефон</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold' }}>Цена</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold' }}>Оплата</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold' }}>Блюда</TableCell>
                                            <TableCell align="center" sx={{ fontWeight: 'bold' }}>Действия</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {Object.values(
                                            eventVisitors.reduce((groups, v) => {
                                                const key = v.order_id || v.id;
                                                if (!groups[key]) groups[key] = { ...v, tickets: [v], total: parseFloat(v.price || 0) };
                                                else { groups[key].tickets.push(v); groups[key].total += parseFloat(v.price || 0); }
                                                return groups;
                                            }, {})
                                        ).map((group) => (
                                            <TableRow key={group.id} hover>
                                                <TableCell>
                                                    {group.visitor_name || '-'}
                                                    {group.tickets.length > 1 && (
                                                        <Chip label={`${group.tickets.length} шт`} size="small"
                                                            color="primary" sx={{ ml: 1 }} />
                                                    )}
                                                </TableCell>
                                                <TableCell>{group.visitor_phone || '-'}</TableCell>
                                                <TableCell>₽{group.total.toLocaleString('ru-RU')}</TableCell>
                                                <TableCell>
                                                    {PAYMENT_METHOD_LABELS[group.payment_method] || group.payment_method || '—'}
                                                </TableCell>
                                                <TableCell>
                                                    {(() => {
                                                        const dishes = extrasForOrder(eventExtras, group.order_id);
                                                        if (!dishes.length) {
                                                            return <Typography variant="body2" color="text.secondary">нет</Typography>;
                                                        }
                                                        const total = extrasTotal(dishes);
                                                        return (
                                                            <Box>
                                                                {dishes.map((dish) => (
                                                                    <Typography key={dish.id} variant="body2">
                                                                        {dish.title} ×{dish.quantity} · {PAYMENT_METHOD_LABELS[dish.payment_method] || dish.payment_method}
                                                                    </Typography>
                                                                ))}
                                                                <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                                                                    {total.toLocaleString('ru-RU')} ₽
                                                                </Typography>
                                                            </Box>
                                                        );
                                                    })()}
                                                </TableCell>
                                                <TableCell align="center">
                                                    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                                                        <IconButton
                                                            size="small"
                                                            color="secondary"
                                                            onClick={() => handleOpenClientDialog(group)}
                                                            title="Карточка клиента"
                                                        >
                                                            <EditIcon />
                                                        </IconButton>
                                                        <IconButton size="small" color="primary"
                                                            onClick={() => handleShowTicketInfo(group.tickets[0].id, group.order_id)}
                                                            disabled={!group.order_id || group.order_id.toString().startsWith('9')}
                                                            title={!group.order_id || group.order_id.toString().startsWith('9') ? 'Добавлен вручную' : 'Информация о билете'}
                                                        ><PictureAsPdfIcon /></IconButton>
                                                        <IconButton size="small" color="error"
                                                            onClick={() => handleOpenDeleteDialog(group.tickets[0])}
                                                            title="Удалить"
                                                        ><DeleteIcon /></IconButton>
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            {eventVisitors.length === 0 && (
                                <Box sx={{ textAlign: 'center', py: 3 }}>
                                    <Typography color="text.secondary">
                                        Нет проданных билетов
                                    </Typography>
                                </Box>
                            )}
                        </Grid>

                        {/* Правая колонка - Меню */}
                        <Grid item xs={12} md={5}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                <Typography variant="h6">🍽️ Заказы гостей</Typography>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        onClick={() => setOpenStaffDialog(true)}
                                        disabled={!token}
                                        title={!token ? 'Нужен вход в систему' : 'Назначить гида/сотрудников на слот'}
                                    >
                                        👤 Гид
                                    </Button>
                                    <Button
                                        variant="text"
                                        size="small"
                                        onClick={handleOpenAddMenuDialog}
                                    >
                                        На кухню без гостя
                                    </Button>
                                </Box>
                            </Box>

                            <Paper variant="outlined" sx={{ p: 2, minHeight: 200 }}>
                                {eventExtras.length === 0 ? (
                                    <Box sx={{ textAlign: 'center', py: 4 }}>
                                        <Typography color="text.secondary">
                                            Блюда гостей пока не добавлены
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                            Добавляйте еду в карточке посетителя или при записи. Так видно, кто заказал и чем платил.
                                        </Typography>
                                    </Box>
                                ) : (
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                        {eventExtras.map((extra) => (
                                            <Card key={extra.id} variant="outlined">
                                                <CardContent sx={{ py: 1.25, '&:last-child': { pb: 1.25 } }}>
                                                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                                                        {getExtraEmoji(extra)} {extra.title} ×{extra.quantity}
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary">
                                                        {extra.visitor_name || 'Гость'} · {PAYMENT_METHOD_LABELS[extra.payment_method] || extra.payment_method} · ₽{Number(extra.total_price || 0).toLocaleString('ru-RU')}
                                                    </Typography>
                                                </CardContent>
                                            </Card>
                                        ))}
                                        <Box sx={{ mt: 1, pt: 1.5, borderTop: 1, borderColor: 'divider' }}>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                                Итого по гостям: ₽{extrasTotal(eventExtras).toLocaleString('ru-RU')}
                                            </Typography>
                                        </Box>
                                    </Box>
                                )}
                            </Paper>

                            {eventMenu.filter((item) => !item.order_extra_id).length > 0 && (
                                <Paper variant="outlined" sx={{ p: 2, mt: 2, bgcolor: '#fafafa' }}>
                                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Кухня без гостя — не продажа</Typography>
                                    {eventMenu.filter((item) => !item.order_extra_id).map((item) => {
                                        const isBundle = item.type === 'bundle';
                                        const displayName = isBundle ? item.bundle_name : item.name;
                                        return (
                                            <Box key={item.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5 }}>
                                                <Typography variant="body2">
                                                    {displayName} ×{item.quantity}
                                                </Typography>
                                                <IconButton size="small" color="error" onClick={() => handleRemoveMenuItem(item.id)}>×</IconButton>
                                            </Box>
                                        );
                                    })}
                                </Paper>
                            )}

                            <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                    <Typography variant="h6">💰 Выручка слота</Typography>
                                    {eventSales.length > 0 && (
                                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                                            Итого: ₽{eventSales.reduce((sum, item) => sum + parseFloat(item.total_amount || 0), 0).toLocaleString('ru-RU')}
                                        </Typography>
                                    )}
                                </Box>

                                {!token ? (
                                    <Typography color="text.secondary">
                                        Для учёта выручки нужен вход в систему.
                                    </Typography>
                                ) : eventStaff.length === 0 ? (
                                    <Typography color="text.secondary">
                                        Сначала назначьте гида или сотрудника на слот, затем записывайте бар и сувенирку.
                                    </Typography>
                                ) : (
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                        <FormControl fullWidth size="small">
                                            <InputLabel>Ответственный сотрудник</InputLabel>
                                            <Select
                                                value={salesDraft.employee_id}
                                                label="Ответственный сотрудник"
                                                onChange={(e) => setSalesDraft({ ...salesDraft, employee_id: e.target.value })}
                                            >
                                                {eventStaff.map((staff) => (
                                                    <MenuItem key={staff.employee_id} value={String(staff.employee_id)}>
                                                        {staff.full_name} ({staff.position || 'сотрудник'})
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>

                                        <Box>
                                            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                                                Сувениры
                                            </Typography>
                                            <Grid container spacing={1} alignItems="center">
                                                <Grid item xs={12} sm={7}>
                                                    <FormControl fullWidth size="small">
                                                        <InputLabel>Сувенир</InputLabel>
                                                        <Select
                                                            value={selectedSouvenirId}
                                                            label="Сувенир"
                                                            onChange={(e) => setSelectedSouvenirId(e.target.value)}
                                                        >
                                                            {availableSouvenirItems.length === 0 ? (
                                                                <MenuItem disabled value="">
                                                                    Нет позиций — добавьте в раздел «Сувенирка»
                                                                </MenuItem>
                                                            ) : (
                                                                availableSouvenirItems.map((item) => (
                                                                    <MenuItem key={item.id} value={String(item.id)}>
                                                                        {item.name} · ₽{Number(item.price).toLocaleString('ru-RU')}
                                                                    </MenuItem>
                                                                ))
                                                            )}
                                                        </Select>
                                                    </FormControl>
                                                </Grid>
                                                <Grid item xs={6} sm={2}>
                                                    <TextField
                                                        fullWidth
                                                        size="small"
                                                        type="number"
                                                        label="Кол-во"
                                                        value={souvenirQty}
                                                        onChange={(e) => setSouvenirQty(Math.max(1, parseInt(e.target.value, 10) || 1))}
                                                        inputProps={{ min: 1 }}
                                                    />
                                                </Grid>
                                                <Grid item xs={6} sm={2}>
                                                    <FormControl fullWidth size="small">
                                                        <InputLabel>Оплата</InputLabel>
                                                        <Select
                                                            value={souvenirPaymentDraft || 'cash'}
                                                            label="Оплата"
                                                            onChange={(e) => setSouvenirPaymentDraft(e.target.value)}
                                                        >
                                                            {paymentOptions.map((method) => (
                                                                <MenuItem key={method} value={method}>
                                                                    {PAYMENT_METHOD_LABELS[method] || method}
                                                                </MenuItem>
                                                            ))}
                                                        </Select>
                                                    </FormControl>
                                                </Grid>
                                                <Grid item xs={12} sm={3}>
                                                    <Button
                                                        fullWidth
                                                        variant="outlined"
                                                        onClick={handleAddSouvenirToCart}
                                                        sx={{ height: '40px' }}
                                                    >
                                                        В корзину
                                                    </Button>
                                                </Grid>
                                            </Grid>
                                        </Box>

                                        {souvenirCart.length > 0 && (
                                            <Paper variant="outlined" sx={{ p: 1.5 }}>
                                                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                                                    Корзина сувениров
                                                </Typography>
                                                {souvenirCart.map((row) => (
                                                    <Box
                                                        key={`${row.souvenir_item_id}-${row.payment_method || 'cash'}`}
                                                        sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5, gap: 1 }}
                                                    >
                                                        <Typography variant="body2" sx={{ flex: 1 }}>
                                                            {row.name} × {row.quantity}
                                                        </Typography>
                                                        <FormControl size="small" sx={{ minWidth: 130 }}>
                                                            <Select
                                                                value={row.payment_method || 'cash'}
                                                                onChange={(e) => handleSouvenirPaymentChange(
                                                                    row.souvenir_item_id,
                                                                    row.payment_method,
                                                                    e.target.value
                                                                )}
                                                            >
                                                                {paymentOptions.map((method) => (
                                                                    <MenuItem key={method} value={method}>
                                                                        {PAYMENT_METHOD_LABELS[method] || method}
                                                                    </MenuItem>
                                                                ))}
                                                            </Select>
                                                        </FormControl>
                                                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                                            ₽{Number(row.line_total).toLocaleString('ru-RU')}
                                                        </Typography>
                                                        <IconButton
                                                            size="small"
                                                            color="error"
                                                            onClick={() => handleRemoveSouvenirFromCart(row.souvenir_item_id, row.payment_method)}
                                                        >
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </Box>
                                                ))}
                                                <Typography variant="subtitle2" sx={{ mt: 1, fontWeight: 'bold' }}>
                                                    Сувениры: ₽{souvenirCartTotal.toLocaleString('ru-RU')}
                                                </Typography>
                                            </Paper>
                                        )}

                                        <Box>
                                            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                                                Бар (кафе / до-после)
                                            </Typography>
                                            <Grid container spacing={1} alignItems="center">
                                                <Grid item xs={12} sm={4}>
                                                    <FormControl fullWidth size="small">
                                                        <InputLabel>Позиция бара</InputLabel>
                                                        <Select
                                                            value={selectedBarItemId}
                                                            label="Позиция бара"
                                                            onChange={(e) => setSelectedBarItemId(e.target.value)}
                                                        >
                                                            {availableMenuItems.length === 0 ? (
                                                                <MenuItem disabled value="">
                                                                    Нет позиций — добавьте в меню
                                                                </MenuItem>
                                                            ) : (
                                                                availableMenuItems.map((item) => (
                                                                    <MenuItem key={item.id} value={String(item.id)}>
                                                                        {item.name} · ₽{Number(item.price).toLocaleString('ru-RU')}
                                                                    </MenuItem>
                                                                ))
                                                            )}
                                                        </Select>
                                                    </FormControl>
                                                </Grid>
                                                <Grid item xs={6} sm={2}>
                                                    <TextField
                                                        fullWidth
                                                        size="small"
                                                        type="number"
                                                        label="Кол-во"
                                                        value={barQty}
                                                        onChange={(e) => setBarQty(Math.max(1, parseInt(e.target.value, 10) || 1))}
                                                        inputProps={{ min: 1 }}
                                                    />
                                                </Grid>
                                                <Grid item xs={6} sm={2}>
                                                    <FormControl fullWidth size="small">
                                                        <InputLabel>Куда</InputLabel>
                                                        <Select
                                                            value={barDestinationDraft}
                                                            label="Куда"
                                                            onChange={(e) => setBarDestinationDraft(e.target.value)}
                                                        >
                                                            <MenuItem value="before_after">До/после</MenuItem>
                                                            <MenuItem value="cafe">В кафе</MenuItem>
                                                        </Select>
                                                    </FormControl>
                                                </Grid>
                                                <Grid item xs={6} sm={2}>
                                                    <FormControl fullWidth size="small">
                                                        <InputLabel>Оплата</InputLabel>
                                                        <Select
                                                            value={barPaymentDraft || 'cash'}
                                                            label="Оплата"
                                                            onChange={(e) => setBarPaymentDraft(e.target.value)}
                                                        >
                                                            {paymentOptions.map((method) => (
                                                                <MenuItem key={method} value={method}>
                                                                    {PAYMENT_METHOD_LABELS[method] || method}
                                                                </MenuItem>
                                                            ))}
                                                        </Select>
                                                    </FormControl>
                                                </Grid>
                                                <Grid item xs={6} sm={2}>
                                                    <Button
                                                        fullWidth
                                                        variant="outlined"
                                                        onClick={handleAddBarToCart}
                                                        sx={{ height: '40px' }}
                                                    >
                                                        В корзину
                                                    </Button>
                                                </Grid>
                                            </Grid>
                                            {barCart.length > 0 && (
                                                <Paper variant="outlined" sx={{ p: 1.5, mt: 1 }}>
                                                    {barCart.map((row) => (
                                                        <Box
                                                            key={`${row.destination}-${row.menu_item_id}-${row.payment_method || 'cash'}`}
                                                            sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5, gap: 1, flexWrap: 'wrap' }}
                                                        >
                                                            <Typography variant="body2" sx={{ flex: 1, minWidth: 140 }}>
                                                                {row.name} × {row.quantity}
                                                            </Typography>
                                                            <FormControl size="small" sx={{ minWidth: 120 }}>
                                                                <Select
                                                                    value={row.destination === 'cafe' ? 'cafe' : 'before_after'}
                                                                    onChange={(e) => handleBarDestinationChange(
                                                                        row.menu_item_id,
                                                                        row.destination,
                                                                        row.payment_method,
                                                                        e.target.value
                                                                    )}
                                                                >
                                                                    <MenuItem value="before_after">До/после</MenuItem>
                                                                    <MenuItem value="cafe">В кафе</MenuItem>
                                                                </Select>
                                                            </FormControl>
                                                            <FormControl size="small" sx={{ minWidth: 130 }}>
                                                                <Select
                                                                    value={row.payment_method || 'cash'}
                                                                    onChange={(e) => handleBarPaymentChange(
                                                                        row.menu_item_id,
                                                                        row.destination,
                                                                        row.payment_method,
                                                                        e.target.value
                                                                    )}
                                                                >
                                                                    {paymentOptions.map((method) => (
                                                                        <MenuItem key={method} value={method}>
                                                                            {PAYMENT_METHOD_LABELS[method] || method}
                                                                        </MenuItem>
                                                                    ))}
                                                                </Select>
                                                            </FormControl>
                                                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                                                ₽{Number(row.line_total).toLocaleString('ru-RU')}
                                                            </Typography>
                                                            <IconButton
                                                                size="small"
                                                                color="error"
                                                                onClick={() => handleRemoveBarFromCart(row.menu_item_id, row.destination, row.payment_method)}
                                                            >
                                                                <DeleteIcon fontSize="small" />
                                                            </IconButton>
                                                        </Box>
                                                    ))}
                                                    {barCartTotals.beforeAfter > 0 && (
                                                        <Typography variant="body2" sx={{ mt: 1 }}>
                                                            До/после: ₽{barCartTotals.beforeAfter.toLocaleString('ru-RU')}
                                                        </Typography>
                                                    )}
                                                    {barCartTotals.cafe > 0 && (
                                                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                                                            В кафе: ₽{barCartTotals.cafe.toLocaleString('ru-RU')}
                                                        </Typography>
                                                    )}
                                                </Paper>
                                            )}
                                        </Box>


                                        <TextField
                                            fullWidth
                                            size="small"
                                            label="Комментарий"
                                            value={salesDraft.notes}
                                            onChange={(e) => setSalesDraft({ ...salesDraft, notes: e.target.value })}
                                            placeholder="Например: сувениры и кофе после экскурсии"
                                        />

                                        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                            <Button
                                                variant="contained"
                                                onClick={handleSaveEventSales}
                                                disabled={salesSaving}
                                            >
                                                {salesSaving ? 'Сохраняю…' : 'Сохранить выручку'}
                                            </Button>
                                        </Box>
                                    </Box>
                                )}

                                <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                                    {eventSales.length === 0 ? (
                                        <Typography variant="body2" color="text.secondary">
                                            Записей по сувенирке и кафе для этого слота пока нет.
                                        </Typography>
                                    ) : (
                                        eventSales.map((sale) => (
                                            <Card key={sale.id} variant="outlined">
                                                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
                                                        <Box>
                                                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                                                                {sale.employee_name || 'Без сотрудника'}
                                                            </Typography>
                                                            <Typography variant="body2" color="text.secondary">
                                                                {sale.employee_position || 'Сотрудник'}
                                                            </Typography>
                                                        </Box>
                                                        <Box sx={{ textAlign: 'right' }}>
                                                            {Number(sale.souvenirs_amount || 0) > 0 && (
                                                                <Typography variant="body2">
                                                                    Сувенирка: ₽{parseFloat(sale.souvenirs_amount || 0).toLocaleString('ru-RU')}
                                                                    {salePaymentTag(sale.payment_method)}
                                                                </Typography>
                                                            )}
                                                            {Number(sale.cafe_amount || 0) > 0 && (
                                                                <Typography variant="body2">
                                                                    Бар (в кафе): ₽{parseFloat(sale.cafe_amount || 0).toLocaleString('ru-RU')}
                                                                    {salePaymentTag(sale.payment_method)}
                                                                </Typography>
                                                            )}
                                                            {Number(sale.bar_before_after_amount || 0) > 0 && (
                                                                <Typography variant="body2">
                                                                    Бар (до/после): ₽{parseFloat(sale.bar_before_after_amount || 0).toLocaleString('ru-RU')}
                                                                    {salePaymentTag(sale.payment_method)}
                                                                </Typography>
                                                            )}
                                                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                                                                Итого: ₽{parseFloat(sale.total_amount || 0).toLocaleString('ru-RU')}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                    {sale.souvenir_lines?.length > 0 && (
                                                        <Box sx={{ mt: 1 }}>
                                                            {sale.souvenir_lines.map((line) => (
                                                                <Box key={line.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                                    <Typography variant="body2" color="text.secondary">
                                                                        · {line.item_name} × {line.quantity} — ₽{Number(line.line_total).toLocaleString('ru-RU')}
                                                                        {line.payment_method && line.payment_method !== sale.payment_method
                                                                            ? ` (${PAYMENT_METHOD_LABELS[line.payment_method] || line.payment_method})`
                                                                            : ''}
                                                                    </Typography>
                                                                    <IconButton size="small" color="error" onClick={() => handleDeleteSouvenirLine(sale, line)}>
                                                                        <DeleteIcon fontSize="small" />
                                                                    </IconButton>
                                                                </Box>
                                                            ))}
                                                        </Box>
                                                    )}
                                                    {sale.bar_lines?.length > 0 && (
                                                        <Box sx={{ mt: 1 }}>
                                                            {sale.bar_lines.map((line) => (
                                                                <Typography key={line.id} variant="body2" color="text.secondary">
                                                                    · {line.destination === 'before_after' ? 'До/после' : 'В кафе'}: {line.item_name} × {line.quantity} — ₽{Number(line.line_total).toLocaleString('ru-RU')}
                                                                    {line.payment_method && line.payment_method !== sale.payment_method
                                                                        ? ` (${PAYMENT_METHOD_LABELS[line.payment_method] || line.payment_method})`
                                                                        : ''}
                                                                </Typography>
                                                            ))}
                                                        </Box>
                                                    )}
                                                    {sale.notes && (
                                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                                            {sale.notes}
                                                        </Typography>
                                                    )}
                                                </CardContent>
                                            </Card>
                                        ))
                                    )}
                                </Box>
                            </Paper>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenEventDialog(false)}>Закрыть</Button>
                </DialogActions>
            </Dialog>

            {/* Диалог назначения сотрудников/гидов на слот */}
            {token && (
                <EventStaffAssignment
                    open={openStaffDialog}
                    onClose={() => {
                        setOpenStaffDialog(false);
                        if (selectedEvent) {
                            fetchEventStaff(selectedEvent.id);
                        }
                        if (selectedDayData?.date) {
                            fetchDayEvents(selectedDayData.date);
                        }
                    }}
                    event={selectedEvent ? { id: selectedEvent.id, title: selectedEvent.name, event_date: selectedEvent.event_date } : null}
                    token={token}
                />
            )}

            {/* Диалог добавления блюда */}
            <Dialog
                open={openAddMenuDialog}
                onClose={() => {
                    setOpenAddMenuDialog(false);
                    setMenuCategoryFilter('');
                    setSelectedMenuItem('');
                }}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>На кухню без гостя</DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                            Это не продажа и не чек гостя. Строка уйдёт только на кухню, в кассу и статистику не попадёт. Еду для посетителя добавляйте в его карточке.
                        </Typography>
                        <Box>
                            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                                Категория
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                <Button
                                    variant={!menuCategoryFilter ? 'contained' : 'outlined'}
                                    onClick={() => {
                                        setMenuCategoryFilter('');
                                        setSelectedMenuItem('');
                                    }}
                                    sx={{
                                        minWidth: 72,
                                        width: 72,
                                        height: 72,
                                        p: 0.5,
                                        flexDirection: 'column',
                                        borderRadius: 1.5,
                                        textTransform: 'none',
                                        lineHeight: 1.2
                                    }}
                                >
                                    <Typography variant="h6" sx={{ fontSize: '1.25rem', mb: 0.25 }}>☰</Typography>
                                    <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>Все</Typography>
                                </Button>
                                {MENU_CATEGORIES.map((category) => {
                                    const isSelected = menuCategoryFilter === category;
                                    return (
                                        <Button
                                            key={category}
                                            variant={isSelected ? 'contained' : 'outlined'}
                                            onClick={() => {
                                                setMenuCategoryFilter(category);
                                                setSelectedMenuItem('');
                                            }}
                                            sx={{
                                                minWidth: 72,
                                                width: 72,
                                                height: 72,
                                                p: 0.5,
                                                flexDirection: 'column',
                                                borderRadius: 1.5,
                                                textTransform: 'none',
                                                lineHeight: 1.2
                                            }}
                                        >
                                            <Typography variant="h6" sx={{ fontSize: '1.25rem', mb: 0.25 }}>
                                                {getExtraEmoji({ item_type: 'item', category })}
                                            </Typography>
                                            <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                                                {category}
                                            </Typography>
                                        </Button>
                                    );
                                })}
                            </Box>
                        </Box>

                        <FormControl fullWidth>
                            <InputLabel>Выберите блюдо</InputLabel>
                            <Select
                                value={selectedMenuItem}
                                label="Выберите блюдо"
                                onChange={(e) => setSelectedMenuItem(e.target.value)}
                            >
                                {filteredMenuItemsForDialog.length === 0 ? (
                                    <MenuItem disabled value="">
                                        {menuCategoryFilter ? 'В этой категории пока нет блюд' : 'Нет доступных блюд'}
                                    </MenuItem>
                                ) : (
                                    filteredMenuItemsForDialog.map((item) => (
                                        <MenuItem key={item.id} value={item.id}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                                                <span>{item.name} ({item.category})</span>
                                                <span style={{ fontWeight: 'bold' }}>₽{parseFloat(item.price).toLocaleString('ru-RU')}</span>
                                            </Box>
                                        </MenuItem>
                                    ))
                                )}
                            </Select>
                        </FormControl>

                        <TextField
                            label="Количество"
                            type="number"
                            value={menuQuantity}
                            onChange={(e) => setMenuQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                            fullWidth
                            inputProps={{ min: 1 }}
                        />

                        {selectedMenuItem && (
                            <Paper sx={{ p: 2, bgcolor: '#f5f5f5' }}>
                                <Typography variant="body2">
                                    {(() => {
                                        const item = filteredMenuItemsForDialog.find(i => i.id === selectedMenuItem)
                                            || availableMenuItems.find(i => i.id === selectedMenuItem);
                                        return item ? (
                                            <>
                                                <strong>{item.name}</strong><br />
                                                {item.description}<br />
                                                <Box sx={{ mt: 1, fontWeight: 'bold' }}>
                                                    Итого: ₽{(parseFloat(item.price) * menuQuantity).toLocaleString('ru-RU')}
                                                </Box>
                                            </>
                                        ) : null;
                                    })()}
                                </Typography>
                            </Paper>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenAddMenuDialog(false)}>Отмена</Button>
                    <Button
                        variant="contained"
                        onClick={handleAddMenuItem}
                        disabled={!selectedMenuItem}
                    >
                        Добавить
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Диалог добавления посетителя */}
            <Dialog
                open={openAddVisitorDialog}
                onClose={() => setOpenAddVisitorDialog(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>Добавить посетителя к слоту</DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
                        {/* Основная информация о посетителе */}
                        <Paper sx={{ p: 2, bgcolor: '#f9f9f9', order: 2 }}>
                            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                                Основная информация
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <TextField
                                    label="Имя посетителя (главный контакт)"
                                    value={newVisitor.visitor_name}
                                    onChange={(e) => setNewVisitor({ ...newVisitor, visitor_name: e.target.value })}
                                    fullWidth
                                    required
                                />

                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            label="Телефон"
                                            value={newVisitor.visitor_phone}
                                            onChange={(e) => setNewVisitor({ ...newVisitor, visitor_phone: e.target.value })}
                                            fullWidth
                                            placeholder="+79001234567"
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            label="Email"
                                            type="email"
                                            value={newVisitor.visitor_email}
                                            onChange={(e) => setNewVisitor({ ...newVisitor, visitor_email: e.target.value })}
                                            fullWidth
                                            placeholder="example@mail.ru"
                                        />
                                    </Grid>
                                </Grid>

                                <FormControl fullWidth>
                                    <InputLabel id="payment-method-label">Способ оплаты</InputLabel>
                                    <Select
                                        labelId="payment-method-label"
                                        id="payment-method-select"
                                        value={newVisitor.payment_method}
                                        label="Способ оплаты"
                                        onChange={(e) => setNewVisitor({ ...newVisitor, payment_method: e.target.value })}
                                    >
                                        {paymentOptions.map((method) => (
                                            <MenuItem key={method} value={method}>
                                                {PAYMENT_METHOD_LABELS[method] || method}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>

                                <Autocomplete
                                    freeSolo
                                    options={awarenessOptions}
                                    value={getAwarenessSourceDisplay(newVisitor.awareness_source) || null}
                                    onChange={(_, newValue) => setNewVisitor({ ...newVisitor, awareness_source: newValue || '' })}
                                    onInputChange={(_, newValue) => setNewVisitor({ ...newVisitor, awareness_source: newValue || '' })}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Откуда узнали"
                                            placeholder="Выберите или введите свой источник"
                                        />
                                    )}
                                />

                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6}>
                                        <FormControl fullWidth>
                                            <InputLabel id="visit-status-label">Статус посещения</InputLabel>
                                            <Select
                                                labelId="visit-status-label"
                                                value={newVisitor.visit_status || ''}
                                                label="Статус посещения"
                                                onChange={(e) => setNewVisitor({ ...newVisitor, visit_status: e.target.value })}
                                            >
                                                <MenuItem value=""><em>Не указано</em></MenuItem>
                                                <MenuItem value="attended">Были</MenuItem>
                                                <MenuItem value="no_show">Не пришли</MenuItem>
                                                <MenuItem value="cancelled">Отмена</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <FormControl fullWidth>
                                            <InputLabel id="payment-status-label">Статус оплаты</InputLabel>
                                            <Select
                                                labelId="payment-status-label"
                                                value={newVisitor.payment_status || ''}
                                                label="Статус оплаты"
                                                onChange={(e) => setNewVisitor({ ...newVisitor, payment_status: e.target.value })}
                                            >
                                                <MenuItem value=""><em>Не указано</em></MenuItem>
                                                <MenuItem value="paid">С оплатой</MenuItem>
                                                <MenuItem value="unpaid">Без оплаты</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                </Grid>

                                <TextField
                                    label="Комментарий"
                                    multiline
                                    rows={3}
                                    fullWidth
                                    value={newVisitor.notes || ''}
                                    onChange={(e) => setNewVisitor({ ...newVisitor, notes: e.target.value })}
                                    placeholder="Дополнительная информация о посетителе..."
                                />
                            </Box>
                        </Paper>

                        {availableTicketsCount > 0 && (
                            <Paper sx={{ p: 2, bgcolor: '#e8f5e9', border: '2px solid #4caf50', order: 1 }}>
                                <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                    🎫 Бронирование создаётся в Яндекс.Билетах
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    Количество билетов сразу резервируется в Яндексе и исчезает из доступного остатка.
                                </Typography>
                                {willCloseYandexRemainder(
                                    selectedEvent?.product_type,
                                    ticketsList.reduce((sum, ticket) => sum + (ticket.quantity || 1), 0)
                                ) && (
                                        <Typography variant="body2" sx={{ mt: 1, color: '#b45309', fontWeight: 600 }}>
                                            {(selectedEvent?.product_type || '').toLowerCase().includes('индивидуал')
                                                || (selectedEvent?.product_type || '').toLowerCase().includes('свидан')
                                                || (selectedEvent?.product_type || '').toLowerCase().includes('школьн')
                                                || (selectedEvent?.product_type || '').toLowerCase().includes('тренинг')
                                                ? 'После записи весь оставшийся слот закроется в Яндексе — записаться на него с сайта будет нельзя.'
                                                : 'Вы бронируете 8 или больше билетов: остаток слота тоже закроется в Яндексе.'}
                                        </Typography>
                                    )}
                            </Paper>
                        )}

                        {/* Список билетов */}
                        <Paper sx={{ p: 2, bgcolor: '#f9f9f9', order: 0 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                    Билеты ({ticketsList.reduce((sum, t) => sum + t.quantity, 0)} шт)
                                </Typography>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    disabled={availableRates.length === 0 && getTicketCatalog(priceList, selectedEvent?.event_date).length === 0}
                                    onClick={() => {
                                        if (isPackageProductType(selectedEvent?.product_type)) {
                                            const firstRate = availableRates[0];
                                            setTicketsList([...ticketsList, { ...ticketFromRate(firstRate, 1), price: 0 }]);
                                            return;
                                        }
                                        const catalog = getTicketCatalog(priceList, selectedEvent?.event_date);
                                        const next = catalog.find((row) => !ticketsList.some((ticket) => String(ticket.crm_price_id) === String(row.id))) || catalog[0];
                                        if (next) {
                                            setTicketsList([...ticketsList, ticketFromCrmRow(next, availableRates, 1)]);
                                            return;
                                        }
                                        setTicketsList([...ticketsList, ticketFromRate(availableRates[0], 1)]);
                                    }}
                                >
                                    + Добавить тариф
                                </Button>
                            </Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                {isPackageProductType(selectedEvent?.product_type)
                                    ? 'Сумма за всю запись — из прайса. Число людей только занимает места в Яндексе.'
                                    : 'Все билеты из «Настройки CRM». Сверху — цены этого дня (будни или выходные). Тариф Яндекса подбирается сам, чтобы занять место. Цену в чеке можно поменять только для этой записи.'}
                            </Typography>

                            {(() => {
                                const isSlot = isPackageProductType(selectedEvent?.product_type);
                                const catalog = getTicketCatalog(priceList, selectedEvent?.event_date);
                                const yandexRates = (availableRates || []).map((r) => ({
                                    id: r.id,
                                    name: r.name,
                                    price: r.price,
                                    is_preferential: r.is_preferential,
                                    discount_percent: r.discount_percent,
                                    fromYandex: true
                                }));

                                return ticketsList.map((ticket, index) => {
                                    const hasSelectedRate = yandexRates.some((option) => String(option.id) === String(ticket.rate_id));
                                    const yandexOptions = !hasSelectedRate && ticket.rate_id != null && ticket.rate_name
                                        ? [...yandexRates, { id: ticket.rate_id, name: ticket.rate_name, price: ticket.price || 0 }]
                                        : yandexRates;

                                    return (
                                        <Paper key={index} sx={{ p: 2, mb: 2, bgcolor: 'white', border: '1px solid #e0e0e0' }}>
                                            <Grid container spacing={2} alignItems="center">
                                                <Grid item xs={12} sm={4}>
                                                    <FormControl fullWidth size="small">
                                                        <InputLabel id={`ticket-type-label-${index}`}>
                                                            {isSlot ? 'Места в Яндексе' : 'Тип билета'}
                                                        </InputLabel>
                                                        <Select
                                                            labelId={`ticket-type-label-${index}`}
                                                            id={`ticket-type-select-${index}`}
                                                            value={isSlot
                                                                ? (ticket.rate_id != null && ticket.rate_id !== '' ? String(ticket.rate_id) : '')
                                                                : (ticket.crm_price_id != null ? String(ticket.crm_price_id) : '')}
                                                            label={isSlot ? 'Места в Яндексе' : 'Тип билета'}
                                                            disabled={isSlot ? availableRates.length === 0 : catalog.length === 0}
                                                            onChange={(e) => {
                                                                const updated = ticketsList.map((row, i) => (i === index ? { ...row } : row));
                                                                if (isSlot) {
                                                                    const selectedOption = yandexOptions.find((o) => String(o.id) === String(e.target.value));
                                                                    const keepPrice = index === 0
                                                                        ? (Number(updated[index].price) || getSlotCatalogPrice(priceList, selectedEvent?.product_type, selectedEvent?.event_date))
                                                                        : 0;
                                                                    updated[index] = {
                                                                        ...updated[index],
                                                                        ...ticketFromRate(selectedOption, updated[index].quantity || 1),
                                                                        price: keepPrice
                                                                    };
                                                                } else {
                                                                    const crmRow = catalog.find((row) => String(row.id) === String(e.target.value));
                                                                    updated[index] = ticketFromCrmRow(crmRow, availableRates, updated[index].quantity || 1);
                                                                }
                                                                setTicketsList(updated);
                                                            }}
                                                        >
                                                            {isSlot
                                                                ? yandexOptions.map((o) => (
                                                                    <MenuItem key={`${o.id || 'static'}-${o.name}`} value={String(o.id ?? o.name)}>
                                                                        {o.name}
                                                                        {o.is_preferential && ' 🎫'}
                                                                    </MenuItem>
                                                                ))
                                                                : catalog.map((row) => (
                                                                    <MenuItem key={row.id} value={String(row.id)}>
                                                                        {row.name} ({Number(row.price).toLocaleString('ru-RU')}₽)
                                                                        {row.time_until ? ` · до ${String(row.time_until).slice(0, 5)}` : ''}
                                                                    </MenuItem>
                                                                ))}
                                                        </Select>
                                                    </FormControl>
                                                </Grid>
                                                <Grid item xs={12} sm={3}>
                                                    <TextField
                                                        label={isSlot && index === 0 ? 'Сумма за запись (₽)' : 'Цена за 1 шт (₽)'}
                                                        type="number"
                                                        size="small"
                                                        value={ticket.price}
                                                        onChange={(e) => {
                                                            const updated = [...ticketsList];
                                                            updated[index].price = parseFloat(e.target.value) || 0;
                                                            setTicketsList(updated);
                                                        }}
                                                        fullWidth
                                                        disabled={isSlot && index > 0}
                                                        inputProps={{ min: 0, step: 50 }}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={2}>
                                                    <TextField
                                                        label="Количество"
                                                        type="number"
                                                        size="small"
                                                        value={ticket.quantity}
                                                        onChange={(e) => {
                                                            const updated = [...ticketsList];
                                                            updated[index].quantity = parseInt(e.target.value) || 1;
                                                            setTicketsList(updated);
                                                        }}
                                                        fullWidth
                                                        inputProps={{ min: 1, max: 20 }}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={2}>
                                                    <Typography variant="body2" color="primary" sx={{ fontWeight: 'bold' }}>
                                                        {isSlot
                                                            ? (index === 0 ? 'тариф слота' : 'места')
                                                            : `${(ticket.price * ticket.quantity).toLocaleString('ru-RU')} ₽`}
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={12} sm={1}>
                                                    {ticketsList.length > 1 && (
                                                        <IconButton
                                                            size="small"
                                                            color="error"
                                                            onClick={() => {
                                                                setTicketsList(ticketsList.filter((_, i) => i !== index));
                                                            }}
                                                        >
                                                            ✕
                                                        </IconButton>
                                                    )}
                                                </Grid>
                                            </Grid>
                                        </Paper>
                                    );
                                });
                            })()}

                            <Paper sx={{ p: 2, bgcolor: '#e3f2fd' }}>
                                <Typography variant="h6">
                                    Итого: {getBookingTicketTotal(ticketsList, selectedEvent?.product_type, priceList, selectedEvent?.event_date).toLocaleString('ru-RU')} ₽
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Всего человек: {ticketsList.reduce((sum, t) => sum + t.quantity, 0)} шт
                                </Typography>
                                {isPackageProductType(selectedEvent?.product_type) && (
                                    <Typography variant="body2" sx={{ mt: 0.5, fontWeight: 600 }}>
                                        {selectedEvent?.product_type}: {getBookingTicketTotal(ticketsList, selectedEvent?.product_type, priceList, selectedEvent?.event_date).toLocaleString('ru-RU')} ₽ за всю запись, не умножается на число людей.
                                    </Typography>
                                )}
                            </Paper>
                        </Paper>

                        <Paper sx={{ p: 2, bgcolor: '#fff8e1', order: 2 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                                Блюда этого гостя
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                Наберите блюда списком и выберите один способ оплаты на весь заказ гостя.
                            </Typography>
                            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                                <InputLabel>Оплата блюд</InputLabel>
                                <Select
                                    value={visitorExtraDraft.payment_method}
                                    label="Оплата блюд"
                                    onChange={(e) => setVisitorExtraDraft((prev) => ({ ...prev, payment_method: e.target.value }))}
                                >
                                    {paymentOptions.map((method) => (
                                        <MenuItem key={method} value={method}>
                                            {PAYMENT_METHOD_LABELS[method] || method}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <Grid container spacing={2} alignItems="center">
                                <Grid item xs={12} sm={7}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>Блюдо</InputLabel>
                                        <Select
                                            value={visitorExtraDraft.menu_item_id}
                                            label="Блюдо"
                                            onChange={(e) => setVisitorExtraDraft((prev) => ({ ...prev, menu_item_id: e.target.value }))}
                                        >
                                            {availableMenuItems.map((entry) => (
                                                <MenuItem key={entry.id} value={entry.id}>
                                                    {getExtraEmoji({ item_type: 'item', category: entry.category })} {entry.name} · ₽{Number(entry.price || 0).toLocaleString('ru-RU')}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={6} sm={2}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        type="number"
                                        label="Кол-во"
                                        value={visitorExtraDraft.quantity}
                                        onChange={(e) => setVisitorExtraDraft((prev) => ({
                                            ...prev,
                                            quantity: Math.max(1, parseInt(e.target.value, 10) || 1)
                                        }))}
                                        inputProps={{ min: 1 }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={3}>
                                    <Button
                                        fullWidth
                                        variant="contained"
                                        onClick={() => {
                                            const item = availableMenuItems.find((entry) => String(entry.id) === String(visitorExtraDraft.menu_item_id));
                                            if (!item) {
                                                alert('Выберите блюдо');
                                                return;
                                            }
                                            const quantity = visitorExtraDraft.quantity || 1;
                                            setVisitorExtras((prev) => [...prev, {
                                                item_type: 'item',
                                                menu_item_id: item.id,
                                                title: item.name,
                                                category: item.category,
                                                unit_price: Number(item.price) || 0,
                                                quantity,
                                                payment_method: visitorExtraDraft.payment_method || newVisitor.payment_method || 'cash',
                                                notes: visitorExtraDraft.notes || null,
                                                total_price: (Number(item.price) || 0) * quantity
                                            }]);
                                            resetVisitorExtraDraft(visitorExtraDraft.payment_method || newVisitor.payment_method || 'cash');
                                        }}
                                    >
                                        Добавить
                                    </Button>
                                </Grid>
                            </Grid>
                            {visitorExtras.length > 0 && (
                                <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                                    {visitorExtras.map((extra, index) => (
                                        <Box key={`${extra.menu_item_id}-${index}`} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                                            <Typography variant="body2">
                                                {extra.title} ×{extra.quantity} · {PAYMENT_METHOD_LABELS[extra.payment_method] || extra.payment_method} · ₽{Number(extra.total_price || 0).toLocaleString('ru-RU')}
                                            </Typography>
                                            <IconButton size="small" color="error" onClick={() => setVisitorExtras((prev) => prev.filter((_, i) => i !== index))}>✕</IconButton>
                                        </Box>
                                    ))}
                                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                                        Еда: ₽{extrasTotal(visitorExtras).toLocaleString('ru-RU')}
                                    </Typography>
                                </Box>
                            )}
                        </Paper>

                        <Paper sx={{ p: 2, bgcolor: '#fff3e0', order: 3 }}>
                            <Typography variant="body2" color="text.secondary">
                                📝 Посетитель будет добавлен к слоту:<br />
                                <strong>{selectedEvent?.name}</strong>
                            </Typography>
                        </Paper>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenAddVisitorDialog(false)}>Отмена</Button>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
                        {bookingDisabledReason && (
                            <Typography variant="caption" color="warning.main" sx={{ maxWidth: 360, textAlign: 'right' }}>
                                {bookingDisabledReason}
                            </Typography>
                        )}
                        <Button
                            variant="contained"
                            onClick={handleAddVisitor}
                            disabled={Boolean(bookingDisabledReason)}
                        >
                            {bookingInProgress ? 'Бронирование…' : 'Забронировать в Яндексе'}
                        </Button>
                    </Box>
                </DialogActions>
            </Dialog>

            {/* Диалог карточки клиента */}
            <Dialog
                open={openClientDialog}
                onClose={() => setOpenClientDialog(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>Карточка клиента</DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Paper sx={{ p: 2, bgcolor: '#f5f5f5' }}>
                            <Typography variant="body2"><strong>Заказ:</strong> {clientDraft.order_id || '-'}</Typography>
                        </Paper>

                        <TextField
                            fullWidth
                            label="Имя"
                            value={clientDraft.visitor_name || ''}
                            onChange={(e) => setClientDraft({ ...clientDraft, visitor_name: e.target.value })}
                        />

                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Телефон"
                                    value={clientDraft.visitor_phone || ''}
                                    onChange={(e) => setClientDraft({ ...clientDraft, visitor_phone: e.target.value })}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Email"
                                    value={clientDraft.visitor_email || ''}
                                    onChange={(e) => setClientDraft({ ...clientDraft, visitor_email: e.target.value })}
                                />
                            </Grid>
                        </Grid>

                        <FormControl fullWidth>
                            <InputLabel id="client-payment-method-label">Способ оплаты</InputLabel>
                            <Select
                                labelId="client-payment-method-label"
                                value={clientDraft.payment_method || 'cash'}
                                label="Способ оплаты"
                                onChange={(e) => setClientDraft({ ...clientDraft, payment_method: e.target.value })}
                            >
                                {paymentOptions.map((method) => (
                                    <MenuItem key={method} value={method}>
                                        {PAYMENT_METHOD_LABELS[method] || method}
                                    </MenuItem>
                                ))}
                                <MenuItem value="yandex">Яндекс</MenuItem>
                            </Select>
                        </FormControl>

                        <TextField
                            fullWidth
                            type="number"
                            label="Количество человек"
                            value={clientDraft.people_count || 1}
                            onChange={(e) => setClientDraft({ ...clientDraft, people_count: Math.max(1, parseInt(e.target.value, 10) || 1) })}
                            inputProps={{ min: 1, max: 40 }}
                            helperText={String(clientDraft.order_id || '').startsWith('9')
                                ? 'Можно увеличить или уменьшить бронь. Лишние места вернутся на слот в Яндексе.'
                                : 'Меняет число человек в уже созданной записи. Если людей станет меньше — лишние билеты аннулируются в Яндексе и места вернутся на слот.'}
                        />

                        <FormControl fullWidth>
                            <InputLabel id="client-visit-status-label">Статус посещения</InputLabel>
                            <Select
                                labelId="client-visit-status-label"
                                value={clientDraft.visit_status || ''}
                                label="Статус посещения"
                                onChange={(e) => setClientDraft({ ...clientDraft, visit_status: e.target.value })}
                            >
                                <MenuItem value=""><em>Не указано</em></MenuItem>
                                <MenuItem value="attended">Были</MenuItem>
                                <MenuItem value="no_show">Не пришли</MenuItem>
                                <MenuItem value="cancelled">Отмена</MenuItem>
                            </Select>
                        </FormControl>

                        <Autocomplete
                            freeSolo
                            options={awarenessOptions}
                            value={getAwarenessSourceDisplay(clientDraft.awareness_source) || null}
                            onChange={(_, newValue) => setClientDraft({ ...clientDraft, awareness_source: newValue || '' })}
                            onInputChange={(_, newValue) => setClientDraft({ ...clientDraft, awareness_source: newValue || '' })}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Откуда узнали"
                                    placeholder="Выберите или введите свой источник"
                                />
                            )}
                        />

                        <FormControl fullWidth>
                            <InputLabel id="client-payment-status-label">Статус оплаты</InputLabel>
                            <Select
                                labelId="client-payment-status-label"
                                value={clientDraft.payment_status || ''}
                                label="Статус оплаты"
                                onChange={(e) => setClientDraft({ ...clientDraft, payment_status: e.target.value })}
                            >
                                <MenuItem value=""><em>Не указано</em></MenuItem>
                                <MenuItem value="paid">С оплатой</MenuItem>
                                <MenuItem value="unpaid">Без оплаты</MenuItem>
                            </Select>
                        </FormControl>

                        <TextField
                            fullWidth
                            multiline
                            rows={3}
                            label="Комментарий"
                            value={clientDraft.notes || ''}
                            onChange={(e) => setClientDraft({ ...clientDraft, notes: e.target.value })}
                        />

                        <Paper variant="outlined" sx={{ p: 2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2, flexWrap: 'wrap' }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                    Блюда этого гостя
                                </Typography>
                                <Typography variant="subtitle2" color="secondary.main" sx={{ fontWeight: 'bold' }}>
                                    Итого: ₽{clientExtras.reduce((sum, extra) => sum + Number(extra.total_price || 0), 0).toLocaleString('ru-RU')}
                                </Typography>
                            </Box>
                            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                                <InputLabel>Оплата блюд</InputLabel>
                                <Select
                                    value={clientDraft.extra_payment_method || clientDraft.payment_method || 'cash'}
                                    label="Оплата блюд"
                                    onChange={(e) => setClientDraft({ ...clientDraft, extra_payment_method: e.target.value })}
                                >
                                    {paymentOptions.map((method) => (
                                        <MenuItem key={method} value={method}>
                                            {PAYMENT_METHOD_LABELS[method] || method}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            {!clientDraft.order_id ? (
                                <Typography variant="body2" color="text.secondary">
                                    Для ручных билетов позиции клиента недоступны, потому что нет order_id.
                                </Typography>
                            ) : (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} md={5}>
                                            <FormControl fullWidth size="small">
                                                <InputLabel>Позиция</InputLabel>
                                                <Select
                                                    value={clientExtraDraft.menu_item_id}
                                                    label="Позиция"
                                                    onChange={(e) => setClientExtraDraft((prev) => ({
                                                        ...prev,
                                                        menu_item_id: e.target.value
                                                    }))}
                                                >
                                                    {availableMenuItems.map((entry) => (
                                                        <MenuItem key={entry.id} value={entry.id}>
                                                            {getExtraEmoji({ item_type: 'item', category: entry.category })} {entry.name} · ₽{Number(entry.price || 0).toLocaleString('ru-RU')}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={6} md={2}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                type="number"
                                                label="Кол-во"
                                                value={clientExtraDraft.quantity}
                                                onChange={(e) => setClientExtraDraft((prev) => ({
                                                    ...prev,
                                                    quantity: Math.max(1, parseInt(e.target.value, 10) || 1)
                                                }))}
                                                inputProps={{ min: 1 }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={3}>
                                            <Button fullWidth variant="contained" onClick={handleAddClientExtra} sx={{ height: '100%' }}>
                                                Добавить
                                            </Button>
                                        </Grid>
                                    </Grid>

                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Комментарий к позиции"
                                        value={clientExtraDraft.notes}
                                        onChange={(e) => setClientExtraDraft((prev) => ({ ...prev, notes: e.target.value }))}
                                    />

                                    {clientExtras.length === 0 ? (
                                        <Typography variant="body2" color="text.secondary">
                                            Позиции клиента пока не добавлены.
                                        </Typography>
                                    ) : (
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                            {clientExtras.map((extra) => (
                                                <Card key={extra.id} variant="outlined">
                                                    <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                                                            <Box>
                                                                <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                                                                    {getExtraEmoji(extra)} {extra.title}
                                                                </Typography>
                                                                <Typography variant="body2" color="text.secondary">
                                                                    {extra.category || (extra.item_type === 'bundle' ? 'Набор' : 'Позиция')} · ₽{Number(extra.unit_price || 0).toLocaleString('ru-RU')} за шт.
                                                                </Typography>
                                                                {extra.notes && (
                                                                    <Typography variant="body2" color="text.secondary">
                                                                        {extra.notes}
                                                                    </Typography>
                                                                )}
                                                            </Box>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                                                <Button
                                                                    size="small"
                                                                    variant="outlined"
                                                                    onClick={() => handleChangeClientExtraQuantity(extra, Number(extra.quantity) - 1)}
                                                                    disabled={Number(extra.quantity) <= 1}
                                                                >
                                                                    -
                                                                </Button>
                                                                <Chip label={`${extra.quantity} шт`} color="primary" size="small" />
                                                                <Button
                                                                    size="small"
                                                                    variant="outlined"
                                                                    onClick={() => handleChangeClientExtraQuantity(extra, Number(extra.quantity) + 1)}
                                                                >
                                                                    +
                                                                </Button>
                                                                <Typography variant="subtitle2" sx={{ minWidth: 90, textAlign: 'right', fontWeight: 'bold' }}>
                                                                    ₽{Number(extra.total_price || 0).toLocaleString('ru-RU')}
                                                                </Typography>
                                                                <IconButton size="small" color="error" onClick={() => handleRemoveClientExtra(extra.id)}>
                                                                    <DeleteIcon />
                                                                </IconButton>
                                                            </Box>
                                                        </Box>
                                                    </CardContent>
                                                </Card>
                                            ))}
                                        </Box>
                                    )}
                                </Box>
                            )}
                        </Paper>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenClientDialog(false)}>Отмена</Button>
                    <Button variant="contained" onClick={handleSaveClientDraft} disabled={!clientDraft.order_id}>
                        Сохранить
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Диалог подтверждения удаления билета */}
            <Dialog
                open={openDeleteDialog}
                onClose={() => {
                    setOpenDeleteDialog(false);
                    setTicketToDelete(null);
                }}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Удаление билета</DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 2 }}>
                        <Typography variant="body1" gutterBottom>
                            Вы действительно хотите удалить билет?
                        </Typography>
                        {ticketToDelete && (
                            <Paper sx={{ p: 2, mt: 2, bgcolor: '#f5f5f5' }}>
                                <Typography variant="body2">
                                    <strong>Посетитель:</strong> {ticketToDelete.visitor_name || '-'}
                                </Typography>
                                <Typography variant="body2">
                                    <strong>Цена:</strong> ₽{parseFloat(ticketToDelete.price).toLocaleString('ru-RU')}
                                </Typography>
                                <Typography variant="body2">
                                    <strong>Тариф:</strong> {ticketToDelete.rate_name}
                                </Typography>
                            </Paper>
                        )}

                        {ticketToDelete && !ticketToDelete.order_id?.toString().startsWith('9') && (
                            <Paper sx={{ p: 2, mt: 2, bgcolor: '#fff3e0', border: '1px solid #ff9800' }}>
                                <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                                    ⚠️ Этот билет куплен через Яндекс.Билеты
                                </Typography>
                                <Typography variant="body2">
                                    Выберите действие:
                                </Typography>
                            </Paper>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions sx={{ flexDirection: 'column', gap: 1, p: 2, alignItems: 'stretch' }}>
                    {ticketToDelete && !ticketToDelete.order_id?.toString().startsWith('9') ? (
                        <>
                            <Button
                                variant="contained"
                                color="error"
                                onClick={() => handleDeleteVisitor(true)}
                                fullWidth
                            >
                                🚫 Аннулировать в Яндексе и вернуть место на слот
                            </Button>
                            <Button
                                variant="outlined"
                                color="warning"
                                onClick={() => handleDeleteVisitor(false)}
                                fullWidth
                            >
                                🗑️ Удалить только из нашей системы
                            </Button>
                            <Button
                                onClick={() => {
                                    setOpenDeleteDialog(false);
                                    setTicketToDelete(null);
                                }}
                                fullWidth
                            >
                                Отмена
                            </Button>
                        </>
                    ) : (
                        <>
                            <Button
                                variant="contained"
                                color="error"
                                onClick={() => handleDeleteVisitor(false)}
                                fullWidth
                            >
                                🗑️ Удалить билет
                            </Button>
                            <Button
                                onClick={() => {
                                    setOpenDeleteDialog(false);
                                    setTicketToDelete(null);
                                }}
                                fullWidth
                            >
                                Отмена
                            </Button>
                        </>
                    )}
                </DialogActions>
            </Dialog>

            <Dialog
                open={openSeatsDialog}
                onClose={() => (openSeatsBusy ? null : setOpenSeatsDialog(false))}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>➕ Добавить билеты в продажу</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {selectedEvent
                            ? `${selectedEvent.name || 'Слот'} · ${String(selectedEvent.event_date || '').slice(0, 10)}`
                            : 'Слот не выбран'}
                    </Typography>
                    {Number(selectedEvent?.slot_close_count) === 0 ? (
                        <Typography variant="body2" sx={{ mb: 2, color: 'info.main' }}>
                            В CRM нет своих служебных закрытий. При нажатии CRM поищет закрытие слота
                            напрямую в Яндекс.Билетах — в том числе сделанное в кабинете Афиши.
                        </Typography>
                    ) : (
                        <Typography variant="body2" sx={{ mb: 2 }}>
                            В CRM закрыто служебными билетами: {Number(selectedEvent?.slot_close_count) || 0} мест
                            {' '}— их можно вернуть в продажу.
                        </Typography>
                    )}
                    <TextField
                        autoFocus
                        fullWidth
                        size="small"
                        type="number"
                        label="Количество мест"
                        value={openSeatsCount}
                        onChange={(e) => setOpenSeatsCount(Math.max(1, parseInt(e.target.value, 10) || 1))}
                        inputProps={{ min: 1, max: 100 }}
                        helperText="Места снимутся со служебного закрытия слота и вернутся в продажу в Яндексе."
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenSeatsDialog(false)} disabled={openSeatsBusy}>
                        Отмена
                    </Button>
                    <Button
                        variant="contained"
                        color="success"
                        onClick={handleAddSeatsToSale}
                        disabled={openSeatsBusy || !selectedEvent?.id}
                    >
                        {openSeatsBusy ? 'Открываю…' : 'Добавить в продажу'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default VisitsPanel;
